/**
 * Wire types for docs/api-contract.md.
 *
 * Domain enums are imported as *types only* from the shared package, so the
 * union the API validates against and the union the UI switches on can never
 * drift. `import type` is erased at build time, so nothing from the server
 * kernel ends up in the browser bundle.
 *
 * Where the contract describes a payload loosely, the extra fields are optional
 * here. A field being optional means "the contract does not promise it", not
 * "ignore it".
 */
import type {
  AccountAdminState,
  AccountObservedState,
  ActionType,
  ApprovalState,
  AssetType,
  Autonomy,
  Channel,
  ClassificationLabel,
  DispatchTarget,
  DslCondition,
  EffectiveAccountHealth,
  EnrollmentState,
  ExportParseMode,
  MailboxKind,
  MessageDirection,
  MessagedChannel,
  MessagedScope,
  ObservationSource,
  ObservationType,
  PolicyScope,
  ProspectCheckWire,
  ProspectStatus,
  QuotaDeniedReason,
  SignalType,
  SuppressionReason,
  UserRole,
  WorkingHours,
} from '@gojibasel/shared'

export type {
  AccountAdminState,
  AccountObservedState,
  ActionType,
  ApprovalState,
  AssetType,
  Autonomy,
  Channel,
  ClassificationLabel,
  DispatchTarget,
  DslCondition,
  EffectiveAccountHealth,
  EnrollmentState,
  ExportParseMode,
  MailboxKind,
  MessageDirection,
  MessagedChannel,
  MessagedScope,
  ObservationSource,
  ObservationType,
  PolicyScope,
  ProspectCheckWire,
  ProspectStatus,
  QuotaDeniedReason,
  SignalType,
  SuppressionReason,
  UserRole,
  WorkingHours,
}

/** An ISO-8601 timestamp string as the API sends it. */
export type IsoDateTime = string
/** A `YYYY-MM-DD` calendar date. */
export type IsoDate = string
export type Uuid = string

// ---------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------

/** Error codes the API can put on the wire, including the two client-only ones. */
export type ErrorWireCode =
  | 'APP_ERROR'
  | 'CONFIG_INVALID'
  | 'VALIDATION_FAILED'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'QUOTA_DENIED'
  | 'SUPPRESSED'
  | 'UPSTREAM_FAILED'
  | 'LLM_ALLOWANCE_SPENT'
  | 'INTERNAL'
  | 'RATE_LIMITED'
  | 'PAYLOAD_TOO_LARGE'
  | 'UNAVAILABLE'
  | 'NETWORK_UNREACHABLE'
  | 'RESPONSE_UNREADABLE'
  | (string & {})

export interface ErrorEnvelope {
  error: {
    code: ErrorWireCode
    message: string
    details?: Record<string, unknown>
  }
}

/** Every list endpoint answers in this shape. Pagination is `?limit=&cursor=`. */
export interface Paginated<T> {
  items: T[]
  total: number
  cursor: string | null
}

/** One validation problem, used by the knowledge editor to underline a line. */
export interface ValidationIssue {
  path: string
  message: string
  line?: number
}

/** `details` on a quota denial. Everything past `reason` is best effort. */
export interface QuotaDeniedDetails {
  reason: QuotaDeniedReason
  personUserId?: Uuid
  brandId?: Uuid
  channelAccountId?: Uuid
  channel?: Channel
  actionType?: ActionType
  used?: number
  cap?: number
  retryAt?: IsoDateTime
  /** On a quiet-days refusal, when those days end. The same moment as `retryAt`. */
  quietUntil?: IsoDateTime
}

/** `details` on a suppression block. Evidence detail only appears for admins. */
export interface SuppressedDetails {
  reason: SuppressionReason
  prospectId?: Uuid
  brandId?: Uuid
  idType?: string
  evidence?: Record<string, unknown>
  blockingBrandId?: Uuid
  blockingBrandName?: string
  clearsAt?: IsoDateTime
}

// ---------------------------------------------------------------------------
// Auth, session, tenancy
// ---------------------------------------------------------------------------

export interface SessionUser {
  id: Uuid
  email: string
  name: string
  role: UserRole
  /** The timezone the daily ceiling rolls over in. */
  tz?: string
  totpEnrolled?: boolean
  /** An https URL or a data: URI. Null or absent draws initials instead. */
  avatarUrl?: string | null
  /** The language this person reads. Approvals translates drafts in any other language into it. */
  readingLanguage?: string | null
  /** The schools on their account, compared with a prospect's Education. */
  schools?: string[]
  /** May switch between the super admin view and the admin view. */
  superAdmin?: boolean
  /** Looking at every brand at once right now. Only ever true for a super admin. */
  superAdminView?: boolean
}

export interface SessionBrand {
  id: Uuid
  name: string
  slug?: string
  /** A brand cannot launch campaigns until its knowledge file is valid. */
  knowledgeValid?: boolean
  knowledgeVersion?: number
  /**
   * The brand mark: sanitised inline SVG, or a bounded `data:` URI for a logo
   * that is artwork rather than a vector. Null or absent draws a monogram
   * instead. `BrandMark` tells the two apart, so nothing else has to.
   */
  logoSvg?: string | null
  /** Archived brands are filtered out of this list before it is sent. */
  status?: 'active' | 'paused' | 'archived'
}

export interface Session {
  user: SessionUser
  brands: SessionBrand[]
  activeBrandId: Uuid | null
  role: UserRole
  /**
   * When the server's session stops working unless it is refreshed (0228). The
   * app renews it from the refresh cookie before then. Null or absent when the
   * server did not say, and nothing is planned.
   */
  sessionExpiresAt?: string | null
}

export interface MfaChallenge {
  mfaRequired: true
  challengeId: string
}

/** `POST /auth/login` either hands back a session or asks for the second factor. */
export type LoginResult = MfaChallenge | Session

export function isMfaChallenge(result: LoginResult): result is MfaChallenge {
  return (result as MfaChallenge).mfaRequired === true
}

export interface TotpEnrollment {
  secret: string
  otpauthUri: string
}

// ---------------------------------------------------------------------------
// Brands, knowledge, assets
// ---------------------------------------------------------------------------

export interface Brand {
  id: Uuid
  name: string
  slug?: string
  createdAt?: IsoDateTime
  memberCount?: number
  knowledgeValid?: boolean
  knowledgeVersion?: number
  /**
   * Archived means retired: out of the brand menu, unable to launch anything,
   * and everything it did still on record. `GET /brands` has always sent it;
   * this type simply never named it.
   */
  status?: 'active' | 'paused' | 'archived'
  /**
   * The brand mark: sanitised inline SVG, or a bounded `data:` URI for a logo
   * that is artwork rather than a vector. Null or absent draws a monogram
   * instead. `BrandMark` tells the two apart, so nothing else has to.
   */
  logoSvg?: string | null
  /** What this brand writes in. Null or absent means work it out per person. */
  defaultLanguage?: string | null
  greetingStyle?: string | null
}

export interface BrandKnowledge {
  brandId: Uuid
  version: number
  valid: boolean
  rawMd: string
  parsed?: Record<string, unknown>
  updatedAt?: IsoDateTime
  updatedBy?: string
  errors?: ValidationIssue[]
  /** Chunk count and model, so the page can show whether retrieval is degraded. */
  chunkCount?: number
  embeddingModel?: string
}

export interface BrandKnowledgeVersion {
  version: number
  valid: boolean
  createdAt: IsoDateTime
  createdBy?: string
  summary?: string
}

export interface Asset {
  id: Uuid
  brandId?: Uuid
  name: string
  assetType: AssetType
  /** Industry this asset lands best in, used by the vertical match when scoring. */
  vertical?: string | null
  url?: string | null
  /** What it is, as opposed to what it is about. Decides how it renders and which channels carry it. */
  mediaKind?: string | null
  description?: string | null
  active?: boolean
  createdAt?: IsoDateTime
}

// ---------------------------------------------------------------------------
// Prospects and intelligence
// ---------------------------------------------------------------------------

export interface ScoreComponent {
  raw: number
  weight: number
  contribution: number
}

/** `prospects.score_breakdown`, the explainable-scoring payload. */
export interface ScoreBreakdown {
  firmographic: ScoreComponent
  persona: ScoreComponent
  signal: ScoreComponent
  recency: ScoreComponent
  total: number
  /** Free-text notes the scorer produced per component, when it has any. */
  notes?: Partial<Record<'firmographic' | 'persona' | 'signal' | 'recency', string>>
}

export interface Signal {
  id: Uuid
  signalType: SignalType
  source: string
  confidence: number | null
  detectedAt: IsoDateTime
  evidence?: Record<string, unknown> | null
  /** Short human line the API may precompute. Falls back to the evidence blob. */
  summary?: string | null
}

export interface Observation {
  id: Uuid
  obsType: ObservationType
  value: Record<string, unknown> | string | boolean | null
  observedAt: IsoDateTime
  source: ObservationSource
  channelAccountHandle?: string | null
  observedByName?: string | null
}

export interface Identity {
  idType: string
  idValue: string
  confidence: number | null
}

export interface JustificationEvidence {
  /** What kind of thing backs the claim. */
  kind: 'signal' | 'icp_attribute' | 'enrichment' | 'observation' | 'manual'
  /** The row or field it points at. */
  ref: string
  label: string
  detail?: string | null
  /** Set when the evidence is a signal we can link to on this page. */
  signalId?: Uuid | null
}

export interface JustificationReason {
  text: string
  evidence: JustificationEvidence[]
}

/**
 * The structured justification the prospect record carries.
 *
 * The approval queue embeds a flattened form of the same row (`Justification`
 * below), because a reviewer reads reasons rather than evidence trees.
 */
export interface JustificationView {
  reasons: JustificationReason[]
  generatedAt: IsoDateTime
  model: string | null
  /** True when a person wrote or edited this rather than the generator. */
  authoredByHand?: boolean
}

/** The flattened justification embedded in an approval item. */
export interface Justification {
  reasons: string[]
  evidenceRefs?: string[]
  generatedAt?: IsoDateTime
  authoredBy?: 'model' | 'human'
  promptVersion?: string
}

/** Why a prospect cannot be contacted. Members see the reason code, admins also see evidence. */
/** A park a workflow put on a person: until when, why, and which campaign did it. */
export interface ParkView {
  until: IsoDateTime
  label: string | null
  campaignId: Uuid | null
  campaignName: string | null
}

/** One park from a person's history, kept after it ends so they can be found again. */
export interface ParkHistoryView {
  campaignId: Uuid | null
  campaignName: string | null
  label: string | null
  parkedAt: IsoDateTime
  until: IsoDateTime
  /** When a member ended it early, or null. */
  liftedAt: IsoDateTime | null
  /** When it stopped holding, or null while it still holds. */
  endedAt: IsoDateTime | null
}

export interface SuppressionView {
  reason: SuppressionReason
  scope: 'global' | 'brand'
  since: IsoDateTime | null
  /** Present only for a hold that lifts by itself. */
  clearsAt?: IsoDateTime | null
  blockingBrandName?: string | null
  /** The row that decided it. Optional so an older answer still satisfies the type. */
  suppressionId?: Uuid
  /** Every live row with the same reason for this person, so lifting it lifts all of it. */
  suppressionIds?: Uuid[]
  /** "Conor", or "Conor, using the key Conor's api key". Null when the platform set it. */
  byName?: string | null
  /** Why, in the words of whoever set it. */
  note?: string | null
  /** Admin-only. The API strips this for members. */
  evidence?: Record<string, unknown> | null
}

/** One channel's most recent outreach to a person, from this brand. */
export interface PriorOutboundView {
  channel: Channel
  actionType: string
  at: IsoDateTime
  campaignId: Uuid | null
  campaignName: string | null
}

/** What this brand already sent a person, and the last thing they sent back. */
export interface PriorContactView {
  outbound: PriorOutboundView[]
  inbound: { channel: Channel; at: IsoDateTime; classification: string | null; source?: string | null } | null
  /**
   * Contact the platform did not send itself: a message sent from a phone or
   * logged by hand, a reply that reached a thread some other way, and a
   * conversation the index knows about. Absent from an older server.
   */
  recorded?: PriorRecordedView[]
}

export interface PriorRecordedView {
  kind: 'outbound' | 'inbound' | 'conversation'
  channel: Channel
  /** Where it was recorded, as the inbox names it: `engine`, `manual_entry`, `instantly`, `mailbox_scan`, and so on. */
  source: string
  at: IsoDateTime
  /** `name` is a thread mirrored from a phone under exactly their name, which nobody has linked to them. */
  matchedBy: 'record' | 'address' | 'name'
}

/** The next thing a campaign intends to do to this person. */
export interface NextActionView {
  campaignId: Uuid
  campaignName: string
  stepIndex: number
  actionType: ActionType
  channel: Channel
  dueAt: IsoDateTime | null
  enrollmentState: EnrollmentState
  /** Set when a limit is holding this step back rather than a schedule. */
  blockedBy?: string | null
  /** One sentence naming what to change to unblock it. Null when nothing is blocking. */
  blockedReason?: string | null
  /** When it started, which is what separates waiting a moment from being stuck for hours. */
  blockedSince?: IsoDateTime | null
  /** The holding campaign's status. A `draft` holds nobody. */
  campaignStatus?: string
}

/**
 * The compact prospect embedded wherever another record points at a person:
 * an approval item, a work card, the reply-logging typeahead.
 */
export interface ProspectSummary {
  id: Uuid
  fullName: string
  title?: string
  company?: string
  email?: string
  linkedinUrl?: string
  status: ProspectStatus
  icpScore?: number
  hasJustification?: boolean
  suppressed?: boolean
  /** A teammate marked as a test recipient. Present only when true. */
  internal?: boolean
  suppressionReason?: SuppressionReason
  lastSignalAt?: IsoDateTime
  listNames?: string[]
}

/**
 * Whether a prospect's email sits on their company's domain, and when it does
 * not, how similar the two are as the model scored it (0 to 100).
 */
export interface EmailDomainCheck {
  verdict: 'match' | 'different' | 'unknown'
  similarity: number | null
  emailDomain: string | null
  companyDomain: string | null
  /** What the address was held against: the company's domain, its name, or nothing. */
  basis: 'domain' | 'name' | 'none'
  /** A personal mailbox such as gmail.com. */
  personal: boolean
  reason: string | null
  scorePending: boolean
  scoreFailed: boolean
  checkedAt: IsoDateTime
}

/** One row of `GET /prospects`. */
export interface ProspectListItem {
  id: Uuid
  fullName: string
  title: string | null
  company: string | null
  linkedinUrl: string | null
  email: string | null
  /**
   * The rest of what a row already carried and the table never drew.
   *
   * Every one of these is a column the picker can turn on, and that is the
   * reason they are here. A field somebody can switch on is how they find out
   * that it is empty for everybody, which is a thing worth knowing before a
   * message is written around it. The statistics beside the table say the same
   * thing in numbers.
   */
  phone: string | null
  location: string | null
  industry: string | null
  companySizeBand: string | null
  language: string | null
  /**
   * What the last verification said about the address. Null when nothing has
   * checked it, which is not the same as an address that failed.
   */
  emailStatus: string | null
  /**
   * The address against the company on file, from the enrichment pipeline.
   * Null until the current address has been judged. Optional because a list
   * answer from before it existed carries none.
   */
  emailDomainCheck?: EmailDomainCheck | null
  status: ProspectStatus
  /** ISO 3166-1 alpha-2. Null is normal and means the language falls back to the name. */
  country?: string | null
  /** State or province postal code (AB, TX), read from the location. Canada and the US only. */
  region?: string | null
  /** Somebody took this row out of consideration. Not a suppression. */
  excluded?: boolean
  /** Who took it out, when and why. Null when it is not taken out. */
  exclusion?: { at: IsoDateTime; byName: string | null; reason: string | null } | null
  icpScore: number | null
  source: string | null
  createdAt: IsoDateTime
  /** Signal types seen on this prospect, newest first. */
  signalTypes: SignalType[]
  hasJustification: boolean
  suppression: SuppressionView | null
  /**
   * Set aside by a workflow that ended by parking them, until the date passes.
   * Null when nobody parked them or the date is behind us. On a list row a park
   * also fills `suppression` when nothing else does, because adding the person
   * to a campaign is refused for it. Optional so a page built before parks
   * existed still reads the rest.
   */
  parked?: ParkView | null
  nextAction: NextActionView | null
  listNames?: string[]
  /**
   * For a teammate only: the apps their record has a handle on. The Team row
   * says from this which steps of a playbook have no address for them. Absent on
   * a prospect and from an older server.
   */
  handleChannels?: string[]
  /**
   * For a teammate only: the handle on each app, so the Team row can show the
   * addresses a test goes to. Absent on a prospect and from an older server.
   */
  handles?: { channel: string; handle: string }[]
  /**
   * For a teammate only: every channel their record can be reached on, as the
   * server reads it. Messenger is here when the record has a name, because a
   * Messenger step finds a teammate by the name on their record. Absent on a
   * prospect and from an older server.
   */
  reachableChannels?: string[]
  /** A teammate an admin marked as a test recipient. Messages to them are real. */
  internal?: boolean
  /** Present only when the list was asked for it with `include=contact`. */
  priorContact?: PriorContactView | null
  /**
   * Set when another campaign under this brand wrote to them within the
   * cooldown, so Add would be refused until `clearsAt`. Worked out by the server
   * from the brand's own setting, and never set for a teammate or for somebody
   * an admin let through. Present only when the list was asked for `contact`.
   */
  earlierContact?: EarlierContactView | null
  /** The newest decline on record, with whether its hold still stands. */
  decline?: DeclineOnRow | null
  /**
   * The picture to draw beside their name: the first of `ProspectDetail.avatars`
   * in the same channel order. Null when no app has shown the device one, and
   * absent from a server that predates profile pictures, which reads the same.
   */
  avatarUrl?: string | null
}

/**
 * One app's profile picture for a person, as the device last saw it.
 *
 * `url` is served by the platform, behind the same session and brand scope as
 * the person, and carries the picture's hash as `v` so a changed picture is a
 * new address and the browser's cached copy of the old one is never shown in
 * its place.
 */
export interface ProspectAvatar {
  channel: string
  url: string
  sha256: string
  mimeType: string
  width: number | null
  height: number | null
  /** When this app first showed the device a picture for them. */
  firstSeenAt: IsoDateTime
  /** The newest visit that saw it, changed or not. */
  observedAt: IsoDateTime
  /** When the picture last differed from the one before it. Null when it never has. */
  changedAt: IsoDateTime | null
}

/** The message that keeps a person out of a new campaign for now, and until when. */
export interface EarlierContactView {
  campaignId: Uuid | null
  campaignName: string | null
  /**
   * How it reached them when no campaign sent it: somebody here wrote to them
   * outside the platform, from a phone, by hand or through Instantly directly
   * (a `messages.source`). Null or absent for a message a campaign sent.
   */
  source?: string | null
  at: IsoDateTime
  clearsAt: IsoDateTime
}

/** A decline behind a list row's status. */
export interface DeclineOnRow {
  reason: string
  at: IsoDateTime
  holdUntil: IsoDateTime | null
  /** False once the hold lapsed or was lifted. */
  holding: boolean
}

export interface EnrollmentHistoryView {
  id: Uuid
  campaignId: Uuid
  campaignName: string
  state: EnrollmentState
  currentStep: number
  entryChannel: Channel | null
  /** Why they started on that channel, as stored when the opening was chosen. Absent on an older server. */
  entryReasons?: string[]
  enrolledAt: IsoDateTime
  nextActionAt: IsoDateTime | null
  endReason?: string | null
}

/** `GET /prospects/:id`. */
/**
 * One thing a person appears to be interested in, and the words it was read from.
 *
 * The quotes are not decoration and must never be dropped from a rendering.
 * This is a model's reading of somebody's public profile, and a topic nobody
 * can check reads as knowledge about a person rather than as an interpretation
 * of four lines of text.
 */
export interface InterestTopicView {
  topic: string
  confidence: string
  /** Verified to appear in the profile. Shown so a reader can judge the topic. */
  evidence: string[]
}

/** `GET /prospects/:id` — what their public profiles say they care about. */
export interface InterestsView {
  summary: string
  topics: InterestTopicView[]
  /** Which apps were read. */
  channels: string[]
  postCount: number
  model: string
  promptVersion: string
  /** When the words were read off a phone. Null on a reading with no evidence date. */
  evidenceAt: IsoDateTime | null
  /** When a model last read those words. Moves on its own when a prompt improves. */
  derivedAt: IsoDateTime
}

/** `POST /prospects/:id/interest-scan` — what asking for a read did. */
export interface InterestScanRequestView {
  requested: string[]
  alreadyQueued: string[]
  unreachable: boolean
  /** Apps they have a handle on where no scout can read for this brand. Nothing was queued there. */
  noReader?: string[]
  /** The same apps, each with the sentence that says what to change. */
  refused?: { channel: string; reason: string }[]
}

/** The newest read asked for on one app, from `GET /prospects/:id`. */
export interface InterestScanView {
  channel: string
  /** `pending`, `dispatched`, `read`, `analysed` or `refused`. */
  state: string
  reason: string | null
  requestedAt: IsoDateTime
  completedAt: IsoDateTime | null
}

/**
 * How recently somebody was active on one app, as the entry router judges it.
 *
 * `recent` and `older` are the footprint weights' two activity windows,
 * `dormant` is a profile that was read and has gone quiet, and `unknown` means
 * no activity date is on file at all.
 */
export type ChannelActivity = 'recent' | 'older' | 'dormant' | 'unknown'

/**
 * `GET /prospects/:id/channel-ranking`, one row per enabled channel.
 *
 * The rows arrive already ordered, usable first. A screen renders them in the
 * order they came, because the order is the router's decision and a second sort
 * on this side is a second opinion nobody asked for.
 */
export interface ChannelRankView {
  channel: Channel
  /** 1-based among usable channels. Null when the channel is not usable. */
  rank: number | null
  usable: boolean
  activity: ChannelActivity
  lastActiveAt: IsoDateTime | null
  /** When a phone last read their profile on this app. */
  lastReadAt: IsoDateTime | null
  score: number
  /** The active brand has an account that sends on this channel. */
  brandCanSend: boolean
  /** Short sentences, most important first. */
  reasons: string[]
}

export interface ChannelRankingAnswer {
  channels: ChannelRankView[]
  generatedAt: IsoDateTime
}

/** `GET /discovery-status`, mirrored from `DiscoveryStatusWire` in the accounts routes. */
export interface DiscoveryStatus {
  /** False means a profile is read only when somebody asks on a person's page. */
  scheduleEnabled: boolean
}

/**
 * What is in a phone's address book, and how sure the platform is of it.
 *
 * Three values and they are not interchangeable, which is why this is a state
 * and not a boolean:
 *
 *   written          the contact was created and the phone was then read back
 *                    and the number was found on it. The only value that means
 *                    a device read confirmed anything.
 *   already_present  the number was on the phone before anything was written,
 *                    so nothing was created and no entry there is ours.
 *   unconfirmed      a contact was created and the read back did not find the
 *                    number. A half written contact, and the one state where
 *                    what is actually on that handset is unknown.
 *
 * There is deliberately no failed value. A write that fell over before creating
 * anything left nothing on the phone, so there is nothing for a record to be a
 * record of, and a row for it would block the retry that should happen.
 *
 * A screen must never draw `unconfirmed` as saved. Doing so would have this
 * console claim a device read that never happened, and the retry it invites is
 * exactly what puts a second copy of somebody in an address book.
 */
export type DeviceContactState = 'written' | 'already_present' | 'unconfirmed'

/**
 * One entry in one phone's address book, as `GET /prospects/:id` sends it.
 *
 * These are the six fields the route puts in each `savedTo` row and no others.
 * Who asked for the write is on the record in the database and is deliberately
 * not on the wire: naming a person needs a users lookup the route does not do,
 * and a field declared here that the server never fills is a row on the screen
 * that never draws, which is worse than an absent one because nobody can tell
 * it apart from a write nobody asked for.
 */
export interface DeviceContactView {
  /** The phone. The id the engine chose for itself rather than a platform record. */
  deviceId: string
  /** The phone by its name, which is what the page shows (owner, 2026-10-26: never the address). */
  phoneName: string
  /**
   * What was written, verbatim, rather than what the record says today. A name
   * corrected tomorrow does not change the contact on the handset, and a view
   * that re-read the current values would quietly start describing an entry
   * that is not there.
   */
  displayName: string
  phoneNumber: string
  state: DeviceContactState
  /** What the device said in its own words. Mostly present on a half write. */
  detail: string | null
  writtenAt: IsoDateTime
}

/**
 * `GET /prospects/:id`, the `contact` block. Where this person stands with
 * every address book the platform has written for this brand.
 *
 * Two fields and they answer two different questions. `phone` is what WOULD be
 * written, reduced by the server the same way the write path reduces it, so it
 * is null for a stored string that cannot be anybody's number. That is why the
 * screen reads the number from here rather than from `phone` on the person: a
 * national spelling with a leading zero is on the record and is not something
 * the platform will put on a handset, and a control that looked enabled and
 * then refused would be a button that lied about itself.
 *
 * `savedTo` is one entry per handset, and it is a list rather than one entry
 * because one brand can have more than one phone. It is usually empty, which is
 * the ordinary state and reads as nobody having asked yet.
 */
export interface ProspectContactStanding {
  phone: string | null
  savedTo: DeviceContactView[]
}

/**
 * What asking for a save did for one person.
 *
 * None of these five values means anything was written. The write happens on
 * the phone long after this answer has come back, so `queued` means the
 * platform accepted the request and an address book has not been touched, and
 * no screen may report it as written.
 *
 * `waiting` is the one worth reading twice, and it is the reason this union has
 * five members rather than four. It means a write for this person is already on
 * its way to that handset and asking again changes nothing. That is a good
 * outcome and it is not a refusal, and a screen that folded it into one would
 * tell somebody their request was turned down when in fact it was already
 * being carried out.
 */
export type SaveContactOutcome =
  | 'queued'
  | 'already_saved'
  | 'waiting'
  | 'no_number'
  | 'refused'

/**
 * One person's answer. The body of `POST /prospects/:id/contact`, and one entry
 * of `results` on `POST /prospects/contacts`.
 *
 * The single-person route answers this with a 200 only when the outcome is
 * `queued`. Every other outcome comes back as a 409 carrying the same sentence
 * and the same machine-readable code in the error, so a screen driving that
 * route reads a refusal off the rejection rather than off a success.
 */
export interface ProspectContactResultView {
  prospectId: Uuid
  /** The person's name on file. Null for an id this brand does not hold. */
  fullName: string | null
  outcome: SaveContactOutcome
  /** One sentence, written for whoever pressed the button. Always present. */
  message: string
  /** What would be written, when there is anything to write. */
  displayName: string | null
  phoneNumber: string | null
  /** The allowance this spent, or null when nothing was asked of a handset. */
  reservationId: string | null
  /** A machine-readable reason, for a screen that branches. Null on success. */
  refusal: string | null
}

/**
 * `POST /prospects/contacts`. One entry per person asked for, plus what the
 * request as a whole did.
 *
 * Refusals are included and by name. A person left out of a bulk action with no
 * name attached is a person somebody has to go and find by hand later, so
 * `results` accounts for everybody in the request rather than only the ones it
 * accepted, and `counts` always sums to the deduplicated list.
 *
 * `minGapMinutes` arrives as the number the policy row holds, and it is on the
 * wire so that the sentence about pacing can be the server's rather than a
 * figure typed into this codebase. Nothing on these screens renders it: the
 * copy says spaced out, which stays true the day somebody tunes the row.
 */
export interface SaveContactBulkResult {
  /** Which handset the writes were asked of. */
  deviceId: string
  deviceLabel: string | null
  minGapMinutes: number
  /** The server's own sentence about the pacing. */
  pacing: string
  counts: {
    queued: number
    alreadySaved: number
    waiting: number
    noNumber: number
    refused: number
  }
  results: ProspectContactResultView[]
}

/**
 * The address on file is a shared inbox, and whether somebody vouched that it
 * reaches this person. Email steps to it are skipped unless `allowed`.
 */
export interface RoleAddressView {
  /** The mailbox name before the `@`. */
  mailbox: string
  allowed: boolean
  at: IsoDateTime | null
  byName: string | null
  note: string | null
}

/** What a list said about a person and their company, each null when nobody said. */
export interface ProspectFacts {
  headline: string | null
  summary: string | null
  companyDescription: string | null
  companyLocation: string | null
  companyType: string | null
  companyFoundedYear: number | null
  companyKeywords: string | null
  employeeCount: number | null
  annualRevenue: string | null
}

/**
 * The AI Ark record a contact was imported from, shaped by the API for the
 * contact page's People Detail layout (owner, 2026-11-02). Null for anybody who
 * came in another way. Dates are `YYYY-MM`.
 */
export interface AiArkRole {
  title: string | null
  employmentType: string | null
  location: string | null
  description: string | null
  start: string | null
  end: string | null
}

export interface AiArkPosition {
  company: string | null
  roles: AiArkRole[]
  start: string | null
  end: string | null
  current: boolean
}

export interface AiArkEducation {
  school: string | null
  degree: string | null
  field: string | null
  start: string | null
  end: string | null
}

export interface AiArkCompany {
  name: string | null
  description: string | null
  domain: string | null
  website: string | null
  linkedinUrl: string | null
  facebookUrl: string | null
  xUrl: string | null
  foundedYear: number | null
  type: string | null
  industry: string | null
  headcount: number | null
  sizeRange: string | null
  headquarters: string | null
  revenue: string | null
  keywords: string[]
  technologies: string[]
  languages: string[]
}

export interface AiArkProfile {
  aiArkId: string
  headline: string | null
  title: string | null
  summary: string | null
  location: string | null
  departments: string[]
  functions: string[]
  seniority: string | null
  employmentType: string | null
  followers: number | null
  connections: number | null
  badges: string[]
  workHistory: AiArkPosition[]
  education: AiArkEducation[]
  skills: string[]
  certifications: { name: string; authority: string | null }[]
  languages: string[]
  company: AiArkCompany | null
  lastUpdated: string | null
}

export interface ProspectDetail extends ProspectListItem {
  /** The AI Ark record they were imported from. See `AiArkProfile`. */
  aiArk?: AiArkProfile | null
  updatedAt?: IsoDateTime
  /**
   * Null for an ordinary address. Absent from an older server, which read the
   * same as null: no control is drawn.
   */
  roleAddress?: RoleAddressView | null
  /** The stored name parts, which the edit dialog starts from. */
  firstName?: string | null
  middleName?: string | null
  lastName?: string | null
  /** The company's website as stored. Absent from an older server. */
  companyDomain?: string | null
  /** The import columns no field claimed, keyed as merge fields are. Absent from an older server. */
  customFields?: Record<string, string>
  /** What a list said about them and their company. Absent from an older server. */
  facts?: ProspectFacts
  scoreBreakdown: ScoreBreakdown | null
  justification: JustificationView | null
  /** Null when nobody has read their profiles yet, which is most people. */
  interests: InterestsView | null
  /** The newest read asked for on each app. Absent from an older server. */
  interestScans?: InterestScanView[]
  /**
   * One handle per channel, as the server has sent all along.
   *
   * Declared here from 2026-08-29. The route has returned it since handles
   * existed and no client type named it, so every reader had to reach past the
   * type to see it -- which is how a page ends up guessing whether somebody is
   * reachable on an app.
   */
  handles?: { channel: Channel; handle: string; source?: string | null }[]
  /**
   * Each app's profile picture, preferred channel first. Optional, and absent
   * reads as none, so a page served before the platform learned to send them
   * still renders.
   */
  avatars?: ProspectAvatar[]
  signals: Signal[]
  observations: Observation[]
  identities: Identity[]
  enrollments: EnrollmentHistoryView[]
  /** The brand's current ICP weights, so the page can say what feedback moves. */
  icpWeights?: { firmographic: number; persona: number; signal: number; recency: number } | null
  feedback?: { fit: boolean; at: IsoDateTime; byName?: string | null } | null
  /**
   * Where they stand with the phones, or absent from an older answer.
   *
   * Optional so an older server that does not send it still satisfies the type.
   * Absent and an empty `savedTo` mean the same thing to the screen: nothing has
   * been written for this person. Saving somebody is independent of messaging
   * them and of any campaign, so this field says nothing about either.
   */
  contact?: ProspectContactStanding | null
  /** Every park a workflow put on them, newest first, ended ones included. Absent from an older server. */
  parkHistory?: ParkHistoryView[]
  /** The brand's hold for a decline, in days, so the page can say it. */
  declineHoldDays?: number
  /** The newest decline recorded for this person under this brand, whether or not its hold still stands. */
  lastDecline?: LastDeclineView | null
  /**
   * The number, where it came from and why contacting it is allowed. Optional
   * so an older server that does not send it is not an error, and absent reads
   * as a number nobody has recorded a reason for.
   */
  phoneRecord?: {
    phone: string | null
    source: string | null
    contactBasis: string
    sourceUrl: string | null
    mayMessage: boolean
  } | null
}

/**
 * `GET /prospects/field-stats`. How much of this brand is actually filled in.
 *
 * The panel this feeds answers the question the column picker raises. Once you
 * can turn a column on, the next thing you want to know is whether turning it
 * on would draw a column of blanks, and no amount of scrolling the table
 * answers that for a hundred people.
 *
 * The histogram counts standard fields only, and `standardFieldCount` is its
 * denominator. Custom fields differ per brand and per import, so a denominator
 * built from them would mean something different for every brand and would move
 * the moment somebody imported a new column. A fixed denominator is what makes
 * "7 of 11" a sentence somebody can act on. Custom fields still appear in
 * `fields` with a group of 'custom', so their fill rate is visible without
 * being allowed to move the measure.
 */
export interface ProspectFieldStats {
  /** Every prospect in this brand that has not been redacted. */
  total: number
  /** How many standard fields the histogram is measured against. */
  standardFieldCount: number
  fields: {
    key: string
    label: string
    group: 'standard' | 'custom'
    filled: number
    /** 0 to 100, one decimal place. */
    percent: number
  }[]
  /** One row per possible number of filled standard fields, 0 to standardFieldCount. */
  completeness: { filled: number; people: number; percent: number }[]
}

export interface ProspectFilters {
  status?: ProspectStatus | ''
  q?: string
  minScore?: number | ''
  signalType?: SignalType | ''
  /** Only people whose industry has a word starting with this, ignoring case. */
  industry?: string
  /**
   * Only people in these countries, as typed: two letter codes separated by
   * commas or spaces. Read with `parseCountryFilter` before it is sent.
   */
  country?: string
  /**
   * Only people in these US states or Canadian provinces, as typed: codes or
   * names separated by commas. Read with `parseRegionFilter` before it is sent.
   */
  region?: string
  listId?: Uuid | ''
  /**
   * People a workflow set aside: `true` is everybody held now, `ending` the ones
   * whose park ends within `parkEndsWithinDays`, `ended` the ones who are back.
   */
  parked?: 'true' | 'ending' | 'ended' | ''
  /** The window for `ending`, in days. */
  parkEndsWithinDays?: number | ''
  /** `true` for the brand's team test recipients alone, `false` for real prospects alone. */
  internal?: 'true' | 'false' | ''
  /** `contact` asks for what this brand already sent each person. */
  include?: 'contact' | ''
  /** `true` narrows to the people this brand recorded a decline for. */
  declined?: 'true' | ''
  limit?: number
  cursor?: string
}

// ---------------------------------------------------------------------------
// CSV import
// ---------------------------------------------------------------------------

/**
 * The fields a CSV column can be mapped onto.
 *
 * The handle fields are here because the import guesses them from the headers
 * and the guess is permanent: a handle is only ever written into an empty slot,
 * so a later upload cannot correct one. A field the wizard cannot show is a
 * guess the operator cannot see or take back, which is the whole reason they
 * are listed rather than left to detection alone.
 */
export const IMPORT_FIELDS = [
  'full_name',
  'first_name',
  'last_name',
  'title',
  'company',
  'linkedin_url',
  'email',
  'domain',
  'location',
  'industry',
  'company_size',
  /**
   * What a list says about the person and their company, added 2026-09-29
   * (0145) for an SMB export whose summaries and company descriptions were
   * being cut to 500 characters as custom fields. Each is a column now.
   */
  'headline',
  'summary',
  'company_description',
  'company_location',
  'company_type',
  'company_founded_year',
  'company_keywords',
  'employee_count',
  'annual_revenue',
  /**
   * The company's own pages (0125). The import read these from headers it
   * recognised, and a column it did not recognise had no field to pick here.
   */
  'company_linkedin_url',
  'company_facebook_url',
  'company_instagram_handle',
  'company_x_handle',
  'company_email',
  // A real column on a prospect, and the one WhatsApp is reached by: the
  // channel has no username, its identity is the phone number. It was missing
  // from this list while the API and the import module both had it, so a file
  // with a phone column had nowhere to put it.
  'phone',
  // Where each number came from and why it may be contacted, for a file that
  // already knows. Without a basis a WhatsApp message to the number is refused.
  'phone_source',
  'phone_contact_basis',
  'phone_source_url',
  'instagram_handle',
  'x_handle',
  'facebook_handle',
  // Telegram, added on 2026-08-25 to the import module and to nothing the
  // operator can see. The guess is permanent here as on every other handle, so
  // a wrong Telegram column stayed wrong: the wizard neither showed the guess
  // nor offered the field, and there was no way to point the import at the
  // right column or away from the wrong one.
  'telegram_handle',
  /**
   * Added 2026-09-19, for the files that carry them and had nowhere to put them.
   *
   * MODELLED, NEVER SENDABLE, and the difference is the whole reason these three
   * are listed apart from the handles above. Nothing has been measured on a
   * handset for any of them, so `ENABLED_CHANNELS` excludes all three and every
   * gate refuses. What a column buys is one spelling and a row of its own: before
   * this, a TikTok column landed in `custom_fields` under whatever the
   * spreadsheet called it, and `tik_tok`, `you_tube` and `youtube` are all in the
   * database right now from one source.
   */
  'pinterest_handle',
  'tiktok_handle',
  'youtube_handle',
] as const
export type ImportField = (typeof IMPORT_FIELDS)[number]

/** Column header to field. A column left out of the map is ignored. */
export type ImportMapping = Record<string, ImportField>

/**
 * A row the import did not turn into a person.
 *
 * There is deliberately no name here. A skip happens before a person exists, so
 * the importer has nothing to name them by, which is usually the reason for the
 * skip in the first place.
 */
export interface ImportSkip {
  row: number
  /** What kind of problem it was. `reason` is the sentence for it. */
  code: string
  reason: string
}

/** `GET /imports/:batchId`. */
export interface ImportBatch {
  batchId: Uuid
  state: 'queued' | 'running' | 'done' | 'failed'
  totalRows: number
  processedRows: number
  created: number
  matched: number
  skipped: number
  skips: ImportSkip[]
  listId: Uuid | null
  listName: string | null
  startedAt: IsoDateTime | null
  finishedAt: IsoDateTime | null
  error?: string | null
  /** True for a file too big to import while the upload waited, which the worker writes in chunks. */
  background?: boolean
  /**
   * What the email check came to, for a file the worker wrote. A small file's
   * upload answer carries this instead. Absent from an older server.
   */
  emailVerification?: {
    queued: number
    alreadyVerified: number
    withoutAddress: number
    markedUnverified: number
    blocked: string | null
    reason: string | null
  }
}

/** `GET /lists` — the named prospect lists a campaign can be pointed at. */
export interface ProspectList {
  id: Uuid
  name: string
  source: string | null
  prospectCount: number
  /** How many of those are ready to enroll right now. */
  readyCount?: number | null
  /** How many of those this brand already has any history with. Absent from an older server. */
  contactedCount?: number | null
  updatedAt: IsoDateTime | null
  /** An Instagram audience list's people by the list they came from, and how many we follow (0141). */
  audience?: { followers: number; likers: number; mutuals: number; following: number; weFollow: number }
}

export interface ReviewQueueItem {
  id: Uuid
  kind: 'identity_match' | 'classification'
  confidence: number
  candidate: Record<string, unknown>
  incumbent?: Record<string, unknown>
  createdAt: IsoDateTime
}

// ---------------------------------------------------------------------------
// Methods and campaigns
// ---------------------------------------------------------------------------

/** `campaigns.status`. Archived is a shelf, not a state the engine acts on. */
export type CampaignStatus = 'draft' | 'running' | 'paused' | 'completed' | 'archived'

/** A branch always has an explicit else arm. An omitted `else` means skip. */
export interface StepCondition {
  /**
   * Null when the step has no gate of its own and the `if` only says where it
   * goes when the platform will not let it run. Typed as always-present until
   * 2026-08-19, which is why the campaign page threw on the first workflow that
   * used one: the type said the field was there and the JSX read it directly.
   */
  cond: DslCondition | null
  /** `goto:<i>`, `end` or `skip`. */
  else: string
  /**
   * Which channel a "have we messaged them yet" check asks about. The API sends
   * it on that check only, resolved, so a reader never has to know the default.
   */
  channel?: MessagedChannel
  /** Whose messages that check counts: this campaign's, or anyone's at the brand. */
  scope?: MessagedScope
  /**
   * What a check over the person's record asks, under the keys the document
   * uses: `signal` and `within_days`, `field`, `op` and `values`, `score`, or
   * the days an activity check counts (`within_days`, `quiet_days`). Sent on
   * those checks only. See `ProspectCheckWire`.
   */
  signal?: ProspectCheckWire['signal']
  within_days?: ProspectCheckWire['within_days']
  field?: ProspectCheckWire['field']
  op?: ProspectCheckWire['op']
  values?: ProspectCheckWire['values']
  score?: ProspectCheckWire['score']
  quiet_days?: ProspectCheckWire['quiet_days']
}

export interface StepWait {
  days?: number
  hours?: number
  minutes?: number
  /**
   * And up to this many minutes more, drawn fresh for each person. Absent is an
   * unvarying wait, which is every sequence written before this existed.
   */
  spreadMinutes?: number
  /**
   * Give or take this whole percentage of the wait, drawn fresh for each person
   * either side of it. New wait blocks carry the brand's default share. Never
   * beside `spreadMinutes`, which the server refuses.
   */
  spreadPercent?: number
}

/**
 * One step of `methods.steps`. A step is either a wait, an action, or an action
 * that waits first. The editor always writes the object form of `if`.
 */
export interface MethodStep {
  i: number
  channel?: Channel
  action?: ActionType
  wait?: StepWait
  /**
   * On the wire the condition is *absent* rather than null when a step only
   * names an else arm, because the API's schema takes `cond` as optional and
   * refuses a null. `StepCondition` uses null for the same idea when reading,
   * so the two shapes are deliberately not the same type.
   */
  if?: {
    cond?: DslCondition
    else?: string
    channel?: MessagedChannel
    scope?: MessagedScope
    /** The If not arm's own steps, run before the arm goes where `else` says. */
    steps?: MethodStep[]
  } & ProspectCheckWire
  /** `generated` asks the copy generator, anything else is a literal note. */
  note?: string
  content?: string
  /** `recommended`, `none`, or an asset id. */
  asset?: string
  target?: string
}

/**
 * One way into a sequence.
 *
 * A sequence with two of these opens in two places: at the top for the people
 * an email reaches, and at a later step for the ones only findable elsewhere.
 * Absent means the ordinary single opening at the first step.
 */
export interface MethodEntry {
  channel: Channel
  /** The `i` of the step this opening starts people at. */
  at: number
  label?: string
}

export interface Method {
  id: Uuid
  name: string
  description: string | null
  channelMix: Channel[]
  entry: 'auto' | Channel
  entries?: MethodEntry[]
  steps: MethodStep[]
  endStates: string[]
  version: number
  isActive: boolean
  authorName?: string | null
  updatedAt?: IsoDateTime | null
  /** How many campaigns currently point at this method. */
  campaignCount?: number | null
}

export interface MethodDraft {
  name: string
  description: string
  entry: 'auto' | Channel
  /** Omitted for a sequence with one opening, so the stored document stays as it was. */
  entries?: MethodEntry[]
  steps: MethodStep[]
  isActive: boolean
}

/**
 * A DSL rule the method broke. `path` locates the step, for example
 * `steps.3.if.else`; the editor also reads `stepIndex` when the API sends one.
 */
export interface MethodValidationIssue {
  path: string
  message: string
  stepIndex?: number
  rule?: string
  /**
   * The server says `warning` for something worth knowing that does not stop a
   * save, and `error` for a rule that failed. Absent reads as an error, because
   * reading an unknown severity as "only a warning" is how a failed rule gets
   * waved through.
   */
  severity?: 'error' | 'warning'
}

export interface MethodValidationResult {
  valid: boolean
  issues: MethodValidationIssue[]
}

export type EnrollmentCounts = Partial<Record<EnrollmentState, number>>

/** Set when a person ceiling or a safety rule is holding the campaign back. */
export interface ThrottleView {
  reason: string
  since: IsoDateTime | null
  retryAt: IsoDateTime | null
  /** Which account or person the limit belongs to, when the API can say. */
  subject?: string | null
}

export interface CampaignSummary {
  id: Uuid
  name: string
  status: CampaignStatus
  autonomy: Autonomy
  methodId: Uuid
  methodName: string
  listId: Uuid | null
  listName: string | null
  tz: string
  /** The one language every message is written in. Required since 0130, so only a very old row reads null. */
  language: string | null
  createdAt: IsoDateTime
  launchedAt: IsoDateTime | null
  counts: EnrollmentCounts
  enrolledTotal: number
  sentTotal: number
  replyCount: number
  /** Replies divided by sends, over prospects only, already computed by the API. */
  replyRate: number | null
  /** The part of the totals above that went to teammates marked as test recipients. Absent from an older server. */
  teamTests?: { people: number; sent: number; replies: number }
  throttle: ThrottleView | null
  /** The links `{{url}}` rotates through. Empty is the feature off. */
  urlPool?: string[]
  createdByName?: string | null
  createdByAvatarUrl?: string | null
}

export interface CampaignStepStats {
  scheduled: number
  sent: number
  pendingApproval: number
  replies: number
  replyRate: number | null
  skipped: number
}

export interface CampaignStepView {
  i: number
  channel: Channel | null
  action: ActionType | null
  wait: StepWait | null
  condition: StepCondition | null
  stats: CampaignStepStats
}

export interface CampaignDetail extends CampaignSummary {
  workingHours: WorkingHours | null
  steps: CampaignStepView[]
  methodSteps: MethodStep[]
}

export interface EnrollmentRow {
  id: Uuid
  prospectId: Uuid
  prospectName: string
  company: string | null
  title: string | null
  state: EnrollmentState
  currentStep: number
  nextActionAt: IsoDateTime | null
  entryChannel: Channel | null
  /** Why the router started them on that channel, as it stored the reasons. Absent from an older server. */
  entryReasons?: string[]
  lastEventAt: IsoDateTime | null
  endReason: string | null
  /** Why a launch did not start this person, naming who has them now, when that is how the row ended. */
  endedBecause?: string | null
  /**
   * For a row another campaign's hold ended: whether adding them again would go
   * in now, and the earlier message an admin's Contact anyway would lift.
   */
  addAgain?: { canAddAgain: boolean; earlierContact: EarlierContactView | null } | null
  blockedBy?: string | null
  blockedReason?: string | null
  blockedSince?: IsoDateTime | null
  /** A teammate marked as a test recipient, so the row can say the messages are real tests. */
  internal?: boolean
}

/**
 * CONTRACT GAP: the pre-launch review payload.
 * Assumed `GET /campaigns/:id/preview` returning generated copy plus the
 * recommended asset per step, so the launch flow reviews real content rather than
 * an empty form.
 */
export interface CampaignPreviewStep {
  i: number
  channel: Channel
  action: ActionType
  wait: StepWait | null
  condition: StepCondition | null
  subject: string | null
  body: string | null
  /** LinkedIn connection notes are capped, so the editor shows the count. */
  charLimit: number | null
  asset: { id: Uuid; name: string; url: string | null; rationale: string } | null
  editable: boolean
}

export interface CampaignReadiness {
  listSize: number
  readyToEnroll: number
  blocked: number
  missingJustification: number
  knowledgeValid: boolean
  /** Rough time to work the whole list at the current daily ceilings. */
  estimatedDays: number | null
}

export interface CampaignPreview {
  campaignId: Uuid
  generatedAt: IsoDateTime | null
  model: string | null
  steps: CampaignPreviewStep[]
  readiness: CampaignReadiness | null
  sampleProspect: { id: Uuid; fullName: string; company: string | null } | null
}

export interface CreateCampaignBody {
  methodId: Uuid
  name: string
  autonomy: Autonomy
  listId: Uuid
  tz: string
  workingHours?: WorkingHours
  /**
   * One language for every message on this campaign.
   *
   * Absent or null and each person's is worked out from what is known about
   * them: their country, then their phone number's dialling code, then the
   * brand's own default.
   */
  language?: string | null
}

/**
 * What every person on a list would be written to in, before anything is sent.
 *
 * The counts are the whole point. "Thirty-one in Indonesian, nine in English"
 * is a sentence somebody can agree or disagree with, whereas a language
 * decision made per person at drafting time is one nobody sees until a message
 * has already gone.
 */
export interface ListLanguagePreview {
  listId: Uuid
  listName: string
  total: number
  /** The override that produced this answer, or null for "decide per person". */
  override: string | null
  brandDefault: string | null
  byLanguage: Array<{
    language: string
    name: string
    endonym: string
    /** False when nobody on the team can read a draft in it. */
    reviewable: boolean
    count: number
    /** How many landed there by each route: country, phone, name, and so on. */
    byEvidence: Record<string, number>
  }>
  samples: Array<{
    prospectId: Uuid
    name: string | null
    language: string
    evidence: string
    confidence: string
    reason: string
    greeting: string | null
  }>
}

/**
 * CONTRACT GAP: enrolling prospects into a campaign has no endpoint.
 * Assumed `POST /campaigns/:id/enrollments { prospectIds }` answering with a
 * per-prospect outcome, because a partial block is the normal case and the UI has
 * to name every prospect it could not take.
 */
export interface EnrollResult {
  enrolled: number
  blocked: Array<{
    prospectId: Uuid
    prospectName: string
    code: string
    reason: string
    heldBy?: { campaignId: Uuid; name: string; status: string; via: 'active_enrollment' | 'recent_outreach'; occurredAt: IsoDateTime | null }
    /** Somebody here wrote to them outside the platform, on this record, which an admin may waive. */
    earlierContactWaivable?: true
    clearsAt?: IsoDateTime
  }>
  /** People who went in whom this brand had already written to, or who had written back. */
  wentInWithHistory?: WentInWithHistory[]
  /**
   * Teammates marked for testing who went in, the channels of this sequence
   * their record has no address for, and the sentence to show. Absent from an
   * older server.
   */
  teammatesWithoutAddress?: Array<{
    prospectId: Uuid
    prospectName: string
    missing: string[]
    /**
     * The same channels, each with why: nothing on the record, a number nobody
     * said may be contacted, or an address found not to work. Absent from an
     * older server.
     */
    gaps?: Array<{ channel: string; reason: TeammateGapReason }>
    sentence: string
  }>
}

/** Why a teammate's record cannot reach one channel, as the server words it. */
export type TeammateGapReason = 'no_address' | 'number_without_basis' | 'not_usable'

/**
 * What a launch answers on top of the campaign or playbook it started.
 *
 * `launchWarnings` are whole sentences, one per part of the sequence some people
 * will not get and why, to show as they are. Absent from an older server.
 */
export interface LaunchWarningsAnswer {
  launchWarnings?: string[]
}

/**
 * What `error.details` carries on a launch refused because it would reach
 * nobody: the same sentences a launch that went ahead would have warned with.
 */
export interface LaunchRefusalDetails {
  warnings?: string[]
  /** On a start through an API key only: the draft it made was deleted. */
  draftRemoved?: boolean
}

/** One person an add put in, with what they had already heard from this brand. */
export interface WentInWithHistory {
  prospectId: Uuid
  prospectName: string
  priorContact: PriorContactView
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

/**
 * A send that was approved, handed over, and never arrived.
 *
 * Present on a queue row only when the send did not succeed, and absent on the
 * drafts that make up almost all of the queue. It is what tells the screen it is
 * looking at the one state this pane never used to have: a message that has
 * already been through approval and still needs a person.
 *
 * WHY IT RIDES ON A QUEUE ROW RATHER THAN ON A LIST OF ITS OWN. A device action
 * leaves the scheduler the moment it is handed to the phone, so when the phone
 * reports a fatal there is nothing routing it back: the allowance is returned,
 * the sequence carries on, and the step reads as done while the person is queued
 * for a follow-up to an opener that never arrived. Measured twice on 2026-08-30
 * on two real people. That needs a decision from somebody, and the queue is
 * already the place a person looks at one message and decides what happens to
 * it. A second screen for the same job is the one nobody opens.
 *
 * `error` is whatever came back, stored exactly as it was said. It is never
 * rewritten or shortened here: "timed out after 25s waiting for
 * conversation_contact_name" is ugly and it is also the only line that tells
 * somebody whether trying again is worth anything.
 *
 * `retry` is decided on the server, by the same function the retry command
 * itself refuses with, and is never re-derived in the browser. So the button can
 * never be offered for something the command would turn down.
 */
/** One thing that happened on the way out, as the platform recorded it. */
export interface DeliveryReport {
  at: IsoDateTime
  /** `engine:pixel-8-multi`, `system:quota-governor`, or a person. */
  actor: string
  action: string
  state: string | null
  releaseReason: string | null
  /** The device's own words. Shown as they came back, never rewritten. */
  evidence: Record<string, unknown> | null
  detail: string | null
}

/** `GET /approvals/:id/delivery`: what happened to a message that did not send. */
export interface DeliveryTrail {
  approvalId: Uuid
  stepId: Uuid
  stepIndex: number
  attempt: number
  error: string | null
  failedAt: IsoDateTime | null
  reservation: {
    state: string
    dispatchTarget: string
    channel: string
    reservedAt: IsoDateTime
    dispatchedAt: IsoDateTime | null
    claimedAt: IsoDateTime | null
    settledAt: IsoDateTime | null
    releaseReason: string | null
  } | null
  account: { handle: string; displayName: string | null } | null
  reports: DeliveryReport[]
}

export interface ApprovalDelivery {
  /** What came back when the send failed, or nothing when nothing was said. */
  error: string | null
  /**
   * When it failed, or null when the stored timestamp could not be read.
   *
   * Nullable because the route coerces rather than asserts. A row whose date is
   * unreadable is still a failure somebody has to see, and dropping it over its
   * timestamp would be the same silence this whole change exists to end.
   */
  failedAt: IsoDateTime | null
  /** How many goes this step has had at this index. The first one is 1. */
  attempt: number
  /**
   * Whether it can go back in the queue, and the sentence to show in place of
   * the button when it cannot. The sentence is written for an operator, so it is
   * rendered as it arrives.
   */
  retry: { allowed: boolean; reason: string | null }
  /** Parked rather than failed: a name mismatch offers Fix and retry and Skip. */
  park?: { kind: 'name_mismatch'; storedName: string; publishedName: string } | null
}

export interface ApprovalItem {
  id: Uuid
  campaignId: Uuid
  campaignName?: string
  enrollmentId: Uuid
  /**
   * The step these words belong to.
   *
   * Nothing on this screen addresses anything by it: approve, reject and retry
   * are all addressed by the draft id above, so the three things a person can do
   * to one message are addressed the same way. It is carried because the step is
   * the thing that succeeded or failed and the draft is only the words that were
   * meant to go out, and a screen that shows a failure without being able to name
   * what failed cannot be joined up with anything else that talks about steps.
   */
  stepId: Uuid
  stepIndex: number
  channel: Channel
  action: ActionType
  state: ApprovalState
  prospect: ProspectSummary
  justification?: Justification | null
  /**
   * What would go out as the subject, which is the operator's edit once there
   * has been one, exactly as `body` below is.
   */
  subject?: string | null
  /**
   * What this channel demands of a subject line, or null when it has none.
   *
   * Null on every channel but email and InMail, and it is what decides whether
   * the review screen offers a subject field at all. Not derivable here:
   * `channel` above is the coarse one, so `linkedin` covers a connection note
   * that forbids a subject and an InMail that requires one.
   *
   * Optional on the type and not on the wire, for the same reason `maxMessages`
   * is. A tab that was open before this shipped holds a response from before it
   * existed and is still approving drafts, and `readSubjectRules` says what the
   * screen does then: it goes on showing the subject and does not offer to edit
   * it, which is exactly what that tab did yesterday.
   */
  subjectRules?: { maxLength: number | null; required: boolean } | null
  body: string
  /** LinkedIn connection notes are capped, so the editor needs the limit. */
  maxLength?: number | null
  /**
   * The most messages one send of this draft can carry, resolved from the
   * brand's safety settings rather than written down anywhere in the browser.
   *
   * Optional on the type and not on the wire. The queue sends it, but a tab that
   * was already open when this shipped is holding a response from before it
   * existed, and that tab is still approving messages. `readMessageRules` says
   * what the screen does when it is missing, and the short version is that it
   * never invents a ceiling.
   */
  maxMessages?: number
  recommendedAsset?: {
    id: Uuid
    name: string
    assetType: AssetType
    url?: string
    rationale: string
  } | null
  alternativeAssets?: { id: Uuid; name: string; assetType: AssetType }[]
  /**
   * Present when generation gave up and left this step for a person to write.
   * `reason` is a sentence; `rules` are what the copy was rejected for, one per
   * attempt. Absent on every draft that was written normally.
   */
  notWritten?: { reason: string; rules: string[] } | null
  /** Set when a check at the moment of sending held these words back. `reason` says what to fix. */
  heldBeforeSending?: { reason: string } | null
  /**
   * For an Instagram message written after a visit: how many posts the visit saw
   * and the one the message names, or null in `named` when it names none of them.
   * Absent on every other draft.
   */
  visit?: { postsSeen: number; named: { caption: string; url: string | null } | null } | null
  variant?: string | null
  /** What this draft was written in. Absent on rows written before languages existed. */
  language?: string | null
  /** Why that language, in one sentence, as it was decided when the draft was written. */
  languageReason?: string | null
  /**
   * What the rewrite that removes the marks of machine writing did to this
   * message.
   *
   * Absent, not false, on a draft written before the pass existed. "This row
   * cannot answer the question" and "the pass did not run" are different facts
   * and the screen says neither when it has neither.
   */
  humanised?: {
    ran: boolean
    changed: boolean
    note: string
    remaining: string[]
  } | null
  /**
   * Set when this row is in the queue because the send did not succeed, and null
   * on an ordinary draft that is simply waiting for a decision.
   *
   * Declared as nullable rather than optional because the server always sends the
   * key. `readDelivery` in the approvals model still reads it through a fallback,
   * for the tab that was open before this shipped and is holding a response from
   * before the key existed. That tab is still approving messages, and the answer
   * it should get for a row it cannot know about is "an ordinary draft", which is
   * exactly what every row in its hand was.
   */
  delivery: ApprovalDelivery | null
  createdAt: IsoDateTime
}

/**
 * What `POST /approvals/:id/retry` answers with.
 *
 * It survived the deletion of the separate "Didn't send" screen because the
 * action moved rather than went away. The retry is now addressed by the draft id,
 * the same id approve and reject take, and it still answers with where the
 * enrollment ended up rather than with a bare acknowledgement, because "it went
 * back in the queue" is only worth anything alongside when the queue will next
 * look at it.
 */
export interface RetryAccepted {
  enrollmentId: Uuid
  state: EnrollmentState
  stepIndex: number
  /** When the scheduler will next look at this enrollment. */
  nextActionAt: IsoDateTime | null
}

// ---------------------------------------------------------------------------
// Manual work queue
// ---------------------------------------------------------------------------

export interface ManualCard {
  id: Uuid
  kind: 'action' | 'inbox_check' | 'connections_check'
  state: 'open' | 'done' | 'skipped' | 'replied' | 'expired' | 'revoked'
  channelAccountId: Uuid
  channelAccountLabel: string
  brandId: Uuid
  brandName: string
  action: ActionType
  reservationId: Uuid
  prospect?: ProspectSummary
  profileUrl?: string
  /** The exact text to paste. Never paraphrase it in the UI. */
  copy?: string
  subject?: string
  asset?: { id: Uuid; name: string; url?: string; rationale?: string } | null
  instructions?: string[]
  /** Threads to sweep, present on an inbox check. */
  threads?: { prospectId: Uuid; prospectName: string; profileUrl?: string; lastSentAt?: IsoDateTime }[]
  /** Pending requests to confirm, present on a connections check. */
  pendingRequests?: { prospectId: Uuid; prospectName: string; profileUrl?: string; sentAt?: IsoDateTime }[]
  openedAt: IsoDateTime
  holdExpiresAt: IsoDateTime
}

export interface CardObservationInput {
  obsType: ObservationType
  value: unknown
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

/**
 * One of a brand's own labels, as it is drawn on a thread.
 *
 * A person's filing rather than the classifier's read. The classifier writes its
 * own enum and never touches these, which is what keeps a machine label and a
 * human one from being mistaken for each other on the same row.
 */
export interface ConversationLabel {
  id: Uuid
  name: string
  /** `#rrggbb`, exactly as the person who made the label chose it. */
  colour: string
}

/**
 * Where a drafted reply has got to.
 *
 * `sending` is the one that is easy to fold into `approved` and must not be. It
 * means the dispatcher accepted the reply and queued it on the sending account,
 * so the allowance is already spent and nothing is on the thread yet. Treating
 * it as "approved, still to send" invites a second press, which reserves twice
 * and puts two copies of one reply in front of the same person.
 */
export type ReplyDraftState = 'pending' | 'approved' | 'sending' | 'sent'

/** Who wrote a reply draft's words. See `draftReply.authoredBy`. */
export type ReplyDraftAuthor = 'human' | 'model'

export interface ConversationSummary {
  id: Uuid
  brandId: Uuid
  brandName?: string
  /**
   * Null on a thread a phone swept from an account's own inbox, where the
   * person on the other end is not on anybody's list. Most of a mirrored inbox
   * is strangers, which is the reason the mirror is worth having, so this is an
   * ordinary value rather than a gap in the record.
   */
  prospectId: Uuid | null
  prospectName: string
  prospectTitle?: string
  prospectCompany?: string
  /** A teammate marked as a test recipient, so a reply is a test coming back. Present only when true. */
  prospectInternal?: boolean
  channel: Channel
  channelAccountId?: Uuid
  channelAccountLabel?: string
  campaignId?: Uuid
  campaignName?: string
  lastMessageAt: IsoDateTime
  /**
   * Present and true when `lastMessageAt` is a reading rather than a record.
   *
   * A chat app puts no time on a message, so a device sweep stamps the thread
   * with the moment it looked. Without this the list drew "just now" against
   * every swept conversation, and a thread whose last message was in August sat
   * at the top of the inbox reading as the freshest thing in it. Absent on
   * email, on Instantly, and on every row stored before the engine could say,
   * which is why it is read strictly and never for truthiness.
   */
  lastMessageAtIsApproximate?: boolean
  lastMessagePreview?: string
  lastDirection?: MessageDirection
  classification?: ClassificationLabel | null
  confidence?: number | null
  /**
   * Open, or filed away in the archive.
   *
   * The archive is what this platform does instead of deleting. `conversations`
   * has allowed the second value since the first migration and nothing had ever
   * written it, so the Delete button in the design writes it here and the
   * ledger keeps everything it always kept.
   */
  state: 'open' | 'archived'
  /**
   * Nobody has marked this thread read.
   *
   * REQUIRED, and that is the fix rather than a style choice. It was declared
   * optional here for a long time while the API had no such field and nothing
   * ever set it, so every read of it was `undefined`, every dot built on it
   * drew nothing, and the whole thing typechecked. `readAt === null` is what it
   * means now, and the server is the side that decides.
   */
  unread: boolean
  readAt: IsoDateTime | null
  /**
   * When somebody asked to see this thread again.
   *
   * NOTHING FIRES ON IT. There is no job behind this and no alarm anywhere in
   * the product, so it is a filter and only a filter: it puts the thread on the
   * Reminders tab from that moment. Copy about it must not promise a ping.
   */
  remindAt: IsoDateTime | null
  /** The brand's own labels on this thread. Empty is the ordinary case, never null. */
  labels: ConversationLabel[]
  /**
   * Who the far side is, in the form the platform addresses them by: an email
   * address on email, the handle the account was reached at everywhere else.
   * On the conversation rather than repeated on every message, so the thread
   * pane picks a message's address by its direction.
   */
  prospectAddress: string | null
  /** The account's own address, which is who an outbound message came from. */
  accountAddress: string | null
  needsAttention?: boolean
  /**
   * Where to fetch the person's picture, or null for the initials. Pictures only
   * come off a phone that viewed the profile, so most threads have none. Optional
   * because a server from before pictures sends no key, which reads as none.
   */
  prospectAvatarUrl?: string | null
  /** A photo of the person, or their company's logo off its website (0174), drawn whole rather than cut to a circle. */
  prospectAvatarKind?: 'photo' | 'logo' | null
}

export interface Message {
  id: Uuid
  direction: MessageDirection
  body: string
  subject?: string | null
  sentAt: IsoDateTime
  /**
   * Whether that stamp is a reading rather than a record.
   *
   * A delivered email and a LinkedIn message both arrive with the moment the
   * sending system recorded. A message a phone pushed often does not: chat apps
   * print a day or a weekday above a bubble, so the engine reports the closest
   * moment it can defend and says that is what it did. Absent on every older
   * server and on every message that has a real stamp, which is why it is read
   * strictly and never for truthiness.
   */
  sentAtIsApproximate?: boolean
  /**
   * Where this message sat in the thread when the phone read it, 0 at the top.
   *
   * The second sort key, and it exists because the first one ties. A device
   * sweep stamps a whole thread with the moment it read it, so ordering on
   * `sentAt` alone leaves the pane at the mercy of whatever order the database
   * returned. The engine now spreads those stamps, which fixes it for every
   * consumer that cannot be found and changed, and this is what the rows
   * written before the spread still carry. Absent on every other source.
   */
  ordinal?: number | null
  /**
   * The phone could not read who wrote this one.
   *
   * A sender label on a chat screen sits above a RUN of messages and is not
   * repeated, so the topmost visible message's label is often scrolled off.
   * `direction` on such a row is the safer reading rather than something
   * observed, and the pane says so instead of printing a name over a message
   * nobody could place. Absent means attributed, which is the only honest
   * default: every row written before this existed came off a path that never
   * reported doubt.
   */
  directionIsUnknown?: boolean
  /**
   * The reactions the phone last saw on this message: which ones and how many,
   * never who left them, because the chip under a bubble does not say. Null
   * when it saw none, and on every message that did not come off a phone.
   */
  reactions?: { emoji: string[]; count: number } | null
  classification?: ClassificationLabel | null
  confidence?: number | null
  source?: string
  authorName?: string
  /**
   * What a reply sent from here carried beside its words, so the thread can
   * show what went out. `html` is the formatted email, already through the
   * server's sanitizer, and the reader draws `body` (its plain text) rather
   * than putting markup into the page. Absent on every older server and on
   * every message that was not an email reply sent with them.
   */
  html?: string | null
  cc?: string[]
  bcc?: string[]
}

/**
 * What a reply draft holds beside its words, on email only: the formatted
 * version, the copies, and whether the earlier messages are quoted under it.
 *
 * OPTIONAL HERE, deliberately, though the server sends all four once it has
 * learned them: a server that has not would otherwise read as a draft with no
 * copies on it while one with copies was on file, and the composer reads each
 * with a fallback (no html, no copies, history on) instead.
 */
export interface ReplyDraftExtras {
  html?: string | null
  cc?: string[]
  bcc?: string[]
  includeHistory?: boolean
}

export interface Conversation extends ConversationSummary {
  /** The thread's subject, absent on every channel that has no subject line. */
  subject: string | null
  messages: Message[]
  /**
   * The picture of the teammate whose account the thread is held from, which
   * every outbound message on it was sent as, or null for their initials. Once
   * on the thread rather than on each message, as the two addresses are.
   */
  accountAvatarUrl?: string | null
  /**
   * The reply this platform has already written for the thread.
   *
   * It has been on this payload since the inbox was built and nothing ever drew
   * it, so every draft the platform wrote sat in a list no screen showed. The
   * composer opens on it now.
   *
   * `reservationId` is non-null once the dispatcher has taken the reply: it
   * names the allowance that was spent, which is what makes a second Send a
   * duplicate rather than a retry.
   */
  draftReply?: (ReplyDraftExtras & {
    id: Uuid
    body: string
    state: ReplyDraftState
    reservationId: string | null
    /**
     * Who wrote the words. Every draft was titled "AI draft" and described as
     * written by the platform, including ones a person typed, which is a claim
     * about authorship this record could not support. Read from the draft's own
     * rationale on the server, defaulting to 'model' only for rows written before
     * the field existed, because those came off the classifier.
     */
    authoredBy: ReplyDraftAuthor
  }) | null
}

/** One drafted reply as every reply-draft route hands it out. */
export interface ReplyDraftRecord extends ReplyDraftExtras {
  id: Uuid
  conversationId: Uuid
  channel: Channel
  subject: string | null
  body: string
  state: ReplyDraftState
  createdAt: IsoDateTime
  approvedAt: IsoDateTime | null
  sentAt: IsoDateTime | null
  /** When the dispatcher took it. Set together with `reservationId`. */
  handedOffAt: IsoDateTime | null
  reservationId: string | null
  authoredBy: ReplyDraftAuthor
}

/**
 * What comes back from handing a draft to the dispatcher.
 *
 * The two states are not two words for the same thing. `sent` means it went
 * inside that call and an outbound message is on the thread now. `sending`
 * means it is queued on the sending account and THE THREAD HAS NOTHING ON IT,
 * so a screen that draws it as a sent message is showing somebody their own
 * unsent draft as correspondence.
 */
export interface ReplySendResult {
  draftId: Uuid
  conversationId: Uuid
  /**
   * A THIRD ANSWER, 'approved', means the send was a rehearsal. With dry run on the
   * dispatcher hands back target 'dry_run', and the route leaves the draft approved
   * rather than marking it sent or writing an outbound message onto the thread, so a
   * rehearsal can never read as correspondence. Typed as the two live outcomes only,
   * this field told the composer a rehearsal was impossible.
   */
  state: 'sent' | 'sending' | 'approved'
  reservationId: string
  dispatchTarget: string
  /** Non-null only when `state` is `sent`. */
  messageId: Uuid | null
  /** The server's own sentence for what happened. Shown as it arrives. */
  summary: string
}

export interface LogReplyInput {
  channelAccountId: Uuid
  prospectId: Uuid
  text: string
  receivedAt: IsoDateTime
}

// ---------------------------------------------------------------------------
// Accounts, policy, safety
// ---------------------------------------------------------------------------

export interface ChannelAccount {
  id: Uuid
  channel: Channel
  label: string
  handle?: string
  ownerUserId: Uuid
  ownerName?: string
  /** The owner's picture, so a list of accounts shows whose they are at a glance. */
  ownerAvatarUrl?: string | null
  /** The account's own profile picture, read off the app's account switcher (0188). Drawn before the owner's. */
  pictureUrl?: string | null
  /**
   * Why a campaign has to be confirmed before it sends as this account, or null.
   *
   * Optional like the fields above it, so a server that does not publish it yet
   * reads as no account being protected, which is where every deployment starts.
   */
  protectedNote?: string | null
  /**
   * The account's own profile address, typed by its owner or an admin, or null.
   * The row links to it in preference to a link built from the handle. Absent
   * from an older server, which reads as none typed.
   */
  profileUrl?: string | null
  /** Opens with Assalamu alaikum where the recipient calls for it too. Absent reads as no. */
  usesSalaam?: boolean
  /** LinkedIn Sales Navigator and LinkedIn Premium. Either one lets the account send InMail. */
  salesNav?: boolean
  premium?: boolean
  /** Somebody is using the account by hand until this instant, or null. Routing skips it until then. */
  manualUseUntil?: string | null
  manualUseByName?: string | null
  manualUseNote?: string | null
  /** Null for a pause a person set, auto:<signal> when the platform set it. */
  manualUseSource?: string | null
  adminState: AccountAdminState
  observedState: AccountObservedState
  effectiveHealth: EffectiveAccountHealth
  warmupStartedAt?: IsoDateTime | null
  /** Null once the ramp has finished, so it is never a number that keeps rising. */
  warmupDay?: number | null
  warmupDays?: number
  openCards?: number
  openHousekeepingCards?: number
  weeklyUsed?: number
  /** Null when nothing this account does carries a weekly ceiling. */
  weeklyCap?: number | null
  weeklyCapAction?: ActionType
  /**
   * Today's time in the app against the budget drawn for today, in minutes.
   * Null on a channel with no daily time budget, absent on an older server.
   */
  appMinutesToday?: { usedMinutes: number; drawnMinutes: number } | null
  brandIds?: Uuid[]
  /**
   * Who last signed this account in or out, when, and what they said.
   *
   * `adminStateBy` is what decides whether the owner is offered a "sign back
   * in" button: they may reverse their own decision and nobody else's. The
   * screen only hides the button; the API enforces the rule.
   */
  adminStateAt?: IsoDateTime | null
  adminStateBy?: Uuid | null
  adminStateByName?: string | null
  adminStateReason?: string | null
  /**
   * Which service sends this mailbox's mail, on email accounts, and null on the
   * channels a phone performs. Shown because it decides how a message leaves:
   * an outreach tool holding the mailbox, or this platform signing in as it.
   */
  provider?: EmailProvider | null
  /**
   * Whether this mailbox has a password saved, and when it was last set.
   *
   * A fact about the credential and never the credential. Nothing in this
   * platform returns a stored password, at any permission level: it is typed
   * once, encrypted, and read only by the process that opens the connection.
   * This is exactly enough for the screen to tell "ready" from "waiting".
   */
  hasPassword?: boolean
  passwordSetAt?: IsoDateTime | null
  createdAt?: IsoDateTime
  /**
   * Whether this account can perform anything at all, and by what route.
   *
   * The server computes it from the same facts the dispatcher uses to choose a
   * performer, so no screen has to infer "can this send" from a green dot and
   * arrive at a kinder answer than the dispatcher will. `by_hand` is LinkedIn's
   * ordinary state under ADR-4 and is not a fault: the work becomes a card.
   */
  reach?: AccountReach
  /** Why an unreachable account cannot act, in words. Null when it can. */
  reachBlocker?: string | null
  /**
   * When a sweep of this account's inbox was last asked for from the console.
   *
   * The platform's own bookkeeping and not a device fact: it is the moment the
   * work was queued, which says nothing about whether a phone has been for it
   * yet. `accounts/sweep.ts` owns every sentence that renders it, and the
   * wording keeps that difference visible.
   */
  lastInboxSweepAt?: IsoDateTime | null
  /** How many phones are driving it. Always zero on a mailbox. */
  enginePhones?: number
  /**
   * What the account is for: `outreach`, `enrichment` (reads profiles and never
   * writes to anybody) or `both`. Optional so an older server is not an error,
   * and absent reads as outreach, the column default.
   */
  purpose?: 'outreach' | 'enrichment' | 'both'
  /** When a new account's quiet days end, or null when they do not apply. */
  quietUntil?: IsoDateTime | null
  /** The account's own timezone, which its warm-up days are counted in and the quiet date is named in. */
  tz?: string
  /** Committed actions over the account's life, housekeeping excluded. */
  committedActions?: number
  /** How many committed actions warm-up promotion asks for. */
  warmupMinActions?: number
  /**
   * The brands that may send as this account under the pin rule. An empty list
   * on an enabled account a phone holds is flagged on the accounts page.
   * Absent on an older server, where nothing is flagged.
   */
  usableByBrandIds?: Uuid[]
  /**
   * The same brands, with whether each has a playbook on this account's
   * channel that is not finished, and its name when the viewer is an
   * administrator. The row flags a phone account nothing sends through.
   */
  usableByBrands?: { id: Uuid; name: string | null; hasPlaybookOnChannel: boolean }[]
  /** For somebody who is not an administrator: some brand they do not belong to may use it. */
  usableElsewhere?: boolean
  /** And one of those has a playbook on its channel. */
  playbookElsewhere?: boolean
  lastActionAt?: IsoDateTime | null
}

/**
 * `GET /brands/:id/channel-readiness`, one row per channel.
 *
 * Mirrors `ChannelReadinessWire` in `apps/api/src/lib/channel-readiness.ts`,
 * which is the authority. Every fact a phone reported travels with the instant
 * it reported it, because a reading with no age is what a dead phone looks
 * like from here.
 */
export interface ChannelReadiness {
  channel: Channel
  pick: 'least-recently-used' | 'random'
  account: { id: Uuid; label: string; handle: string } | null
  pool: { id: Uuid; label: string }[]
  /** The scout profiles are read through for this brand, or nothing when no scout can read. */
  readers: { id: Uuid; label: string }[]
  reach: AccountReach | null
  identity: { verifiedAs: string | null; readAt: IsoDateTime | null; lastSeenAt: IsoDateTime | null } | null
  lastActionAt: IsoDateTime | null
  lifetimeCommitted: number
  observedState: string | null
  quietUntil: IsoDateTime | null
  /** The chosen account's timezone, so the quiet date is named on its own calendar. */
  tz: string | null
  blockedReason: string | null
}

export interface ChannelReadinessAnswer {
  brandId: Uuid
  at: IsoDateTime
  channels: ChannelReadiness[]
}

/**
 * The apps a scout can be picked for.
 *
 * Each is scouted by an account on the same app. A Messenger account is never a
 * Facebook scout, because the Messenger app cannot open a profile. Narrowed
 * from `Channel` rather than written out, so a rename on
 * the kernel side is a compile error here instead of a value no table knows.
 */
export type ScoutChannel = Extract<Channel, 'instagram' | 'x' | 'facebook'>

/**
 * `GET /accounts/scouts`, one row per scout channel, always all three.
 *
 * `reads` is the server's answer to whether profiles on that app are actually
 * read through this scout, which also asks whether the phone can read the app
 * at all. `status` is the sentence the page shows, written by the server so the
 * page never works out a second, kinder answer.
 */
export interface ScoutView {
  channel: ScoutChannel
  channelAccountId: Uuid | null
  handle: string | null
  /**
   * The picked account's own app. `messenger` on the Facebook row is a pick left
   * from before Messenger stopped scouting, and the row says to replace it.
   */
  accountChannel: Channel | null
  reads: boolean
  status: string
}

/** An account whose purpose allows reading, with the scout channel it could serve. */
export interface ScoutCandidate {
  id: Uuid
  channel: Channel
  handle: string
  purpose: 'outreach' | 'enrichment' | 'both'
  /** `enabled` or `disabled`. A string on the wire, so an unfamiliar value reads as a state rather than a crash. */
  adminState: string
  /** `warming`, `healthy`, `throttled` or `flagged`, typed loosely for the same reason. */
  observedState: string
  scoutChannel: ScoutChannel
}

export interface ScoutsAnswer {
  scouts: ScoutView[]
  candidates: ScoutCandidate[]
}

/**
 * How an account's actions actually get performed.
 *
 * Mirrors `accountReach` in `@gojibasel/shared`, which is the only place the
 * rules live. Kept as a literal union here rather than imported because the web
 * bundle takes its types from this file.
 */
export type AccountReach = 'sends_itself' | 'by_hand' | 'unreachable';

/**
 * Which service sends an email account's mail.
 *
 * `smtp` is the mailbox signing in as itself with an app password, which is the
 * only one of the three where this platform is the sender rather than a caller.
 */
export type EmailProvider = 'instantly' | 'autoklose' | 'smtp'

export interface QuotaPolicy {
  id: Uuid
  scope: PolicyScope
  /** Null means the policy applies to every subject in the scope. */
  subjectId?: Uuid | null
  subjectLabel?: string
  channel: Channel
  actionType: ActionType
  capDaily: number
  capWeekly: number
  jitterLo: number
  jitterHi: number
  minGapMinutes: number
  /** The range a day's cap is drawn from. Shown instead of the stored cap. */
  dayRange?: { low: number; high: number } | null
  version?: number
  updatedAt?: IsoDateTime
  updatedBy?: string
}

export interface SafetySettings {
  scope: 'global' | 'brand' | 'account'
  subjectId?: Uuid | null
  maxOpenCards: number
  holdTtlManualCardHours: number
  holdTtlEngineHours: number
  autoWithdrawDays: number
  warmupDays: number
  crossBrandCooldownDays: number
  /**
   * How many messages from the person make a thread a relationship rather than
   * a lead. Optional here because it is one of the settings this console reads
   * and never writes, so an older server that does not send it is not an error.
   */
  minInboundMessagesForConversation?: number
  weeklyFreezeRatio: number
  /**
   * Whether a campaign may be put on autopilot at all.
   *
   * Optional, like the two settings below it, so an older server that does not
   * send it is not an error. `autopilotUnlocked()` in lib/autopilot.ts is
   * fail-closed and reads absence as locked, which is the right way round: the
   * cost of being wrong here is a campaign sending without anybody reading it.
   *
   * It went missing rather than being left out. The field existed in
   * `SafetySettings` on the server and was enforced there, and the route that
   * answers this object hand-lists its fields and never listed this one, so the
   * console asked a question the wire could not answer and got "locked" every
   * time.
   */
  autopilotUnlocked?: boolean
  /**
   * When a handset may be given work, on the phone's own clock.
   *
   * `windows: null` is the gate off. `timezone: null` is the sending account's
   * own zone, which for a device account is the phone's. Optional like the
   * settings above it, so a server that does not publish it yet is not an error,
   * and the screen that edits it says so rather than drawing an empty window.
   */
  deviceAwakeHours?: { windows: { days: number[]; start: string; end: string }[] | null; timezone: string | null }
  /** The handset's night, on the phone's own clock. Null is off. Absent on a server that predates it. */
  deviceBlackout?: { days: number[]; start: string; end: string } | null
  /**
   * Each phone's nightly sleep (0215): bedtime drawn in `start`, waking in `end`
   * the next morning, per phone and per night, on the phone's own clock. Null is
   * off. Absent on a server that predates it.
   */
  deviceSleep?: {
    start: { earliest: string; latest: string }
    end: { earliest: string; latest: string }
    windDownMinutes: number
  } | null
  /**
   * The stop that stops everything, and why somebody stopped it.
   *
   * Optional like the settings around it, so a server that does not publish it
   * reads as sending being on, which is the state every deployment starts in.
   */
  sendingHalt?: { stopped: boolean; reason: string | null }
  /** How long one message to a person blocks the same message to them. Zero is off. */
  duplicateActionWindowSeconds?: number
  /** The run of failures that stops an account, and whether it is taken out of service. */
  failureCircuitBreaker?: { consecutiveFailures: number; freezes: boolean }
  /**
   * How many campaigns may run at once. Null is no limit, which is different
   * from zero: zero would mean nothing may ever launch.
   *
   * Optional so an older server that does not send it is not an error, like the
   * two above. `null` and absent both read as no limit on this screen.
   */
  maxRunningCampaigns?: number | null
  /**
   * How many idle cycles a day an account may be given. Null is none, and none
   * is where this starts: it drives a real phone on a real account.
   */
  idleCyclesPerDay?: number | null
  /** How many times a day a LinkedIn account reads its own post analytics. Null is none. */
  postAnalyticsReadsPerDay?: number | null
  /** How many running campaigns one account may send for. Null is no limit. */
  maxRunningCampaignsPerAccount?: number | null
  /** Whether the day and campaign hours run on the phone's clock. */
  phoneClock?: boolean
  /** Whether a campaign's hours also hold back phone work. */
  campaignHoursHoldPhoneWork?: boolean
  /**
   * How far either side of its written figure a new wait block lets each
   * person's wait land, as a whole percentage. The workflow editor gives every
   * wait block it creates this share. Optional so an older server that does not
   * send it is not an error, and absent reads as no spread.
   */
  waitSpreadPercent?: number
  /**
   * The floor under every gap, in seconds. Not a pacing number: it is the point
   * below which two actions on one handset stop describing a person, read by the
   * watcher that reports what nobody could have done.
   */
  minSecondsBetweenActionsOnOneDevice?: number
  /**
   * How many of a person's posts one like or comment visit reacts to, as the two
   * ends of the band the phone draws from. Optional like the settings above, so
   * a server that does not send them is not an error.
   */
  postsPerVisitLow?: number
  postsPerVisitHigh?: number
  /** The owner's Instagram visit (0136). Optional like the band above. */
  instagramVisitPostsLow?: number
  instagramVisitPostsHigh?: number
  instagramCommentsPostsLow?: number
  instagramCommentsPostsHigh?: number
  instagramLinkChance?: number
  instagramStoryChance?: number
  instagramSearchShare?: number
  instagramVisitSecondsLow?: number
  instagramVisitSecondsHigh?: number
  /** The audience read on an Instagram visit (0141). */
  instagramAudienceRowsPerVisit?: number
  instagramAudienceSeeMorePerVisit?: number
  instagramAudienceSeeMorePauseSecondsLow?: number
  instagramAudienceSeeMorePauseSecondsHigh?: number
  instagramAudienceVisitSeconds?: number
  instagramAudienceRowsPerDay?: number
  instagramAudienceRowsPerDayRamped?: number
  instagramAudienceRampCleanDays?: number
  /** How many posts a visit may look at. A step's own count is lowered to it. */
  postsScanBound?: number | null
  /**
   * The watcher's other numbers. One person's accounts moving from one handset
   * to another faster than this, in seconds, is reported, and the three
   * lockstep numbers say when two campaigns count as keeping one beat. All four
   * report and none of them stops anything. Optional so an older server that
   * does not send them is not an error.
   */
  minSecondsBetweenHandsetsForOneOwner?: number
  lockstepToleranceSeconds?: number
  lockstepMinActions?: number
  lockstepLookbackHours?: number
  inboxCheckPerDay: number
  /**
   * How fast a mailbox this platform sends from itself may go.
   *
   * The only pacing here that protects a credential rather than an allowance. A
   * mailbox somebody else holds is paced by whoever holds it. This one is paced
   * by us or by nobody, and the cost of nobody is the address locked out of its
   * own inbox. Optional so an older server that does not send them is not an
   * error, the way the conversation threshold is.
   */
  selfSentSendsPerDay?: number
  selfSentMinGapMinutes?: number
  /** Days a new phone account opens no conversations. Optional so an older server is not an error. */
  warmupQuietDays?: number
  /** Committed actions warm-up promotion asks for. */
  warmupMinActions?: number
  /** Hours ahead that discovery reads people about to be contacted. */
  discoveryLookaheadHours?: number
  /**
   * The pace chain: every delay between one person and the next.
   *
   * Optional like the rest of this interface, because the browser must render
   * against a server that has not been deployed yet. The Pace tab falls back to
   * the seeded value and says which fields it is guessing at, rather than
   * showing a total computed from holes.
   */
  schedulerTickSeconds?: number
  enginePollWaitSeconds?: number
  enginePollIdleLowSeconds?: number
  enginePollIdleHighSeconds?: number
  /** An ESTIMATE. Nothing measures how long a visit takes. Say so wherever it is shown. */
  deviceWorkMinutesLow?: number
  deviceWorkMinutesHigh?: number
  /** Enforced on the handset, chosen here: the pause when one phone swaps identities. */
  deviceMinSwitchSeconds?: number
  /** Minutes an account stays paused after the engine last saw a person using it. */
  humanUseIdleMinutes?: number
  /** LinkedIn's daily app time band in minutes. Both null is no time budget. */
  linkedinAppMinutesLow?: number | null
  linkedinAppMinutesHigh?: number | null
  /** The band each day's share of a weekly limit is drawn from. Both null is no spread. */
  weeklySpreadLow?: number | null
  weeklySpreadHigh?: number | null
  updatedAt?: IsoDateTime
  updatedBy?: string
}

/**
 * One tool registered against one sending account.
 *
 * The row carries two different kinds of fact and they must not be read as one.
 *
 * `active`, `registeredAt` and `registeredBy` are the **platform's** view: this
 * side decided the registration is current, and this side can change it.
 *
 * `deviceId`, `androidUser` and `profileName` are the **admin's signpost**: where
 * the account is supposed to live. They are typed at register time and never
 * move on their own.
 *
 * `deviceActive`, `identityVerifiedAs`, `identityVerifiedAt` and `blockedReason`
 * are the **device's** view, and they are the only fields here the phone itself
 * produced. Nothing in this app can ask for them: the engine is pull-only, it
 * calls the platform and the platform never calls it, so each one is the last
 * thing the device said and not the current state of anything. A reader must
 * render every one of them with how old it is, and must never describe one as
 * live, current, now or connected. A stale check-in is exactly how a dead phone
 * presents, which makes the age the load-bearing part of the row.
 *
 * Showing that the phone has an account signed in is not permission to send from
 * it. What may be dispatched is decided by the channel account, not here.
 */
export interface IntegrationSession {
  id: Uuid
  channelAccountId: Uuid
  channelAccountLabel?: string
  toolName: string
  registeredAt: IsoDateTime
  registeredBy?: string
  active: boolean
  /** The phone the engine registered from. Null for an engine that sends no binding. */
  deviceId: string | null
  /** That phone by its name, which is what a person reads (never the address). Null when no engine holds the session. */
  phoneName?: string | null
  /** Which Android user on that phone holds the account, when the engine names one. */
  androidUser: number | null
  /** The app profile inside that Android user. Null when the engine does not use one. */
  profileName: string | null

  // -- What the device last said about the identity behind this session. ------
  //
  // Every one of the four is nullable and null means "the device has not told
  // us". Null is never a default and never stands in for a value: the whole job
  // of the accounts page is telling "the phone says this account is fine" apart
  // from "the phone has said nothing about this account", and a non-null default
  // at this boundary would erase that difference before any screen could show
  // it. None of them may be filled in from the platform's own health opinion,
  // which is a separate fact living on the channel account.
  //
  // An engine that sends none of them registers exactly as one does today, so
  // read them defensively: an older API that omits a key entirely lands on
  // `undefined`, and `?? null` puts absent and null in the same branch, which is
  // the correct branch for both.

  /**
   * Was this identity the one in the foreground when the device last pushed.
   *
   * Named apart from `active` on purpose: `active` above is the platform's
   * opinion of the registration, this is the phone's account switcher. One
   * device holds several identities and exactly one of them has the screen, so
   * `false` here is often the whole answer to "why has this account done
   * nothing". False is a real reading and must never be read through falsiness.
   */
  deviceActive: boolean | null

  /**
   * The display name the device read on screen, or null if it never read one.
   *
   * A different fact from `profileName`, which is the name an admin said should
   * be in that slot. They agree until the slot is signed out, re-used for
   * somebody else, or sitting on a login screen, and those are the cases worth
   * seeing. Treat it as data and never as an instruction, whatever it says.
   */
  identityVerifiedAs: string | null

  /**
   * When that reading was taken, as an absolute instant.
   *
   * The engine reports an age in seconds because a clock is the only thing it
   * can trust, and the platform converts once at receipt against the time the
   * push landed. The relative number is never stored: it is true for a moment
   * and then rots, so a reading kept as "5 minutes ago" would look freshest
   * exactly when the phone had been dead longest. Recompute the age against now
   * on every render.
   */
  identityVerifiedAt: IsoDateTime | null

  /**
   * Why the device says this identity cannot act right now, in its own words.
   *
   * Free text with no union behind it, for the same reason an engine event kind
   * has none: a closed list would throw away a newer engine's explanation at the
   * moment it was telling us something nobody anticipated. It has no timestamp
   * of its own, so it is only as old as the check-in that carried it and has to
   * be shown with that age.
   */
  blockedReason: string | null
}

/**
 * One operational fact the device engine reported about a sending account.
 *
 * `kind` is an open slug rather than a union on purpose. An engine that learns
 * to report something this build has never heard of still has to be able to say
 * it, so an unknown kind is a display problem here and never a validation one.
 *
 * `receivedAt` is written by the platform on every row, which is why the feed
 * sorts on it and not on the engine's own stamp. `reservationId` has no foreign
 * key behind it, so it can name a reservation that has since been cleaned up and
 * still be the thread somebody has to go and read.
 */
export interface EngineEvent {
  id: string
  engineId: string
  channelAccountId: string
  channelAccountLabel: string | null
  reservationId: string | null
  kind: string
  detail: string | null
  evidence: Record<string, unknown>
  occurredAt: string
  receivedAt: string
  acknowledgedAt: string | null
  acknowledgedByName: string | null
}

// ---------------------------------------------------------------------------
// What an engine last said it can do
// ---------------------------------------------------------------------------

/**
 * One verb, described by the engine that performs it.
 *
 * `actionType` is a plain string and not `ActionType`, which is the whole point
 * of rendering the engine's own document rather than a table kept on this side.
 * An engine can name a verb this build has never heard of, and the honest render
 * of one is its own name and its own caveat. Narrowing this to the platform's
 * vocabulary would drop exactly the rows worth reading.
 *
 * `caveat` is the field to render next to the verb rather than behind a
 * disclosure. Three of them carry facts a summary loses: a profile view proves
 * arrival and is visible to the person viewed, a connections check can never
 * prove acceptance, and a connection request is conditional because the app does
 * not offer Connect on every profile.
 *
 * Every field is optional because the manifest is stored exactly as it was
 * posted and nothing validates its inside. Read defensively.
 */
export interface EngineManifestAction {
  actionType?: string
  kind?: string
  payload?: { required?: string[]; optional?: string[] }
  /** The words this verb can answer with, in the engine's vocabulary. */
  results?: string[]
  summary?: string
  caveat?: string
  [key: string]: unknown
}

/**
 * The engine's account of itself, as posted.
 *
 * This is the one object on the engine surface that is not strict, and the
 * looseness is deliberate: its purpose is to describe verbs, payload fields,
 * result words and caveats this platform has not been taught, so a shape tight
 * enough to refuse an unfamiliar key would refuse the newer engine it exists to
 * describe. The index signature says so in the type rather than leaving a page
 * author to discover it.
 *
 * `notPerformed` is a list of verbs the engine does not do, and a reader must
 * render it differently from a verb that is simply absent. Refused is a decision
 * somebody made for a reason. Absent is silence.
 */
export interface EngineManifestDocument {
  engineContractVersion?: string
  tool?: string
  capabilities?: string[]
  transport?: { model?: string; note?: string; endpoints?: Record<string, string>; [key: string]: unknown }
  actions?: EngineManifestAction[]
  notPerformed?: string[]
  [key: string]: unknown
}

/**
 * One engine's latest manifest, from `GET /engine-manifests`.
 *
 * **Two ages, and they answer different questions.** `reportedAt` is when this
 * description arrived. `lastSeenAt` is when that engine last spoke at all. A
 * manifest from Tuesday belonging to an engine that checked in a minute ago
 * describes something running now, and the same manifest from an engine silent
 * since Tuesday describes something that stopped. Neither may be rendered
 * without its age, because this platform cannot ask a phone anything: the engine
 * pulls, it calls in and is never called, so every row here is a push that
 * landed in the past and nothing more.
 *
 * **It is not permission.** A verb listed here says the phone knows the gesture.
 * What may be dispatched is settled by the enabled channels and the platform's
 * own action vocabulary, and neither of them reads this.
 *
 * An empty list is a real answer. It means no engine has described itself, and
 * the honest render is to say so rather than to fall back to a verb table
 * written on this side.
 */
export interface EngineManifest {
  engineId: string
  /** The handset, when the engine names one. */
  deviceId: string | null
  version: string | null
  /** When this description arrived. */
  reportedAt: IsoDateTime
  /** When this engine last spoke at all, which is a different question. */
  lastSeenAt: IsoDateTime | null
  manifest: EngineManifestDocument
}

// ---------------------------------------------------------------------------
// Operator actions
// ---------------------------------------------------------------------------

/**
 * Rehearse runs the navigation, the gates and, for a message, the real typing,
 * and then clears the box. Deliver is the same path carried through.
 *
 * A rehearsal is not a preview and copy must not sell it as one: opening a
 * thread marks it read and fires a read receipt to the person on the other end,
 * so something did reach them even though nothing was delivered.
 */
export type OperatorActionMode = 'rehearse' | 'deliver'

/**
 * Where the request has got to on this side.
 *
 * Deliberately the platform's lifecycle only, with the engine's verdict kept
 * apart in `result`. Folding them into one enum is how a screen ends up saying
 * an action finished because the phone answered, when what the phone answered
 * was that it could not tell.
 *
 * `queued` is honest about the wait. The engine long-polls on its own schedule,
 * so a click is picked up when the phone next asks for work rather than at once,
 * and `cancelled` exists because that gap is a real chance to revoke.
 */
export type OperatorActionState = 'queued' | 'claimed' | 'reported' | 'cancelled' | 'refused'

/**
 * The engine's own word for what happened, left in the engine's vocabulary.
 *
 * The open union is there because a newer engine may answer something this build
 * has never seen, and dropping it into a default bucket would be the platform
 * inventing a verdict. `unconfirmed` is neither a success nor a failure and no
 * caller may collapse it into either.
 */
export type KnownOperatorActionResult = 'sent' | 'unconfirmed' | 'failed' | 'dry-run'
export type OperatorActionResult = KnownOperatorActionResult | (string & {})

/**
 * One device action a named person asked for, outside any campaign.
 *
 * It is the same verb, the same gates and the same ledger as campaign work. What
 * differs is that somebody is named on it, which is the point: the answer to
 * "who made this phone message that person" has to survive that person leaving.
 *
 * `result` and `resultDetail` stay null until the phone answers, and null there
 * means the phone has not answered rather than that nothing happened.
 */
export interface OperatorAction {
  id: Uuid
  brandId: Uuid
  brandName?: string
  channelAccountId: Uuid
  channelAccountLabel?: string | null
  channel: Channel
  actionType: ActionType
  mode: OperatorActionMode
  state: OperatorActionState
  /** The reservation this became, or null when nothing was ever reserved. */
  reservationId?: Uuid | null
  requestedByName?: string
  requestedAt: IsoDateTime
  claimedAt?: IsoDateTime | null
  reportedAt?: IsoDateTime | null
  result?: OperatorActionResult | null
  /** The engine's own sentence about the result. Free text, never a code. */
  resultDetail?: string | null
  /** Whatever the engine attached, screenshots and read-backs included. */
  evidence?: Record<string, unknown>
  /** The payload as it was submitted, so a later reader sees what was asked for. */
  payload?: Record<string, unknown>
  /** What the request named, kept apart from the payload so a list can show it. */
  target?: { name?: string | null; handle?: string | null; url?: string | null }
  /**
   * Why the platform refused, when it refused before anything reached the phone.
   *
   * A refusal is an ordinary answer here and not a fault. Suppression is the one
   * that is never an operator's to override.
   */
  refusedReason?: string | null
  /**
   * How many actions are ahead of this one in the single queue that owns the
   * phone. Null when the platform does not know, which is not zero.
   */
  queuePosition?: number | null
  dispatchTarget?: DispatchTarget | null
}

export interface MailboxConnection {
  id: Uuid
  address: string
  kind: MailboxKind
  authKind: 'imap' | 'gmail_oauth'
  active: boolean
  lastScannedAt?: IsoDateTime | null
  userId?: Uuid
  ownerName?: string
  ownerAvatarUrl?: string | null
  indexedConversations?: number
}

export interface Suppression {
  id: Uuid
  reasonCode: SuppressionReason
  scope: 'global' | 'brand'
  brandId?: Uuid | null
  brandName?: string
  idType: string
  idValue?: string | null
  prospectId?: Uuid | null
  prospectName?: string
  createdAt: IsoDateTime
  createdBy?: string
  /** Who `createdBy` is, as a person reads it. Null when the platform wrote it. */
  createdByName?: string | null
  expiresAt?: IsoDateTime | null
  /** Admin-only. Members see the reason code alone. */
  evidence?: Record<string, unknown>
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/** One row of the cross-brand person quota view. */
export interface QuotaUsageRow {
  personUserId: Uuid
  personName: string
  channel: Channel
  actionType: ActionType
  cap: number
  used: number
  /** Reservations still holding a counter increment. */
  openHolds: number
  remaining: number
  /** How much of `used` each brand is responsible for. */
  byBrand: { brandId: Uuid; brandName: string; used: number }[]
  resetsAt?: IsoDateTime
}

/**
 * A week's usage for one person and verb.
 *
 * Separate from the daily row because the ceiling is genuinely optional here:
 * most verbs carry a daily cap and no weekly one, and a missing weekly ceiling
 * has to stay missing all the way to the screen. Flattening it to zero says
 * the opposite of what it means, which is that nothing is left.
 */
export interface QuotaWeeklyRow extends Omit<QuotaUsageRow, 'cap' | 'remaining'> {
  /** Null when no scope sets a weekly ceiling for this verb. */
  cap: number | null
  /** Null exactly when `cap` is. */
  remaining: number | null
}

export interface QuotaAnalytics {
  day: IsoDate
  timezone?: string
  rows: QuotaUsageRow[]
  weekly?: QuotaWeeklyRow[]
}

export interface CampaignAnalyticsRow {
  key: string
  label: string
  sent: number
  replies: number
  positives: number
  hostiles: number
  replyRate: number
  positiveRate: number
}

export interface CampaignAnalytics {
  byCampaign: CampaignAnalyticsRow[]
  byMethod: CampaignAnalyticsRow[]
  byAsset: CampaignAnalyticsRow[]
  bySignalType: CampaignAnalyticsRow[]
  bySenderAccount: CampaignAnalyticsRow[]
}

export interface AccountHealthRow {
  channelAccountId: Uuid
  label: string
  channel: Channel
  ownerName?: string
  effectiveHealth: EffectiveAccountHealth
  warmupDay?: number
  warmupDays?: number
  weeklyUsed: number
  weeklyCap: number
  failures7d: number
  openCards: number
  oldestOpenCardAt?: IsoDateTime | null
}

export interface EnrichmentAnalytics {
  month: string
  brandId: Uuid
  brandName?: string
  budgetUsd: number | null
  spentUsd: number
  byProvider: { provider: string; operation: string; units: number; costUsd: number }[]
}

export interface ReconciliationResult {
  day: IsoDate
  ranAt: IsoDateTime
  ok: boolean
  rows: {
    personUserId: Uuid
    personName: string
    channel: Channel
    actionType: ActionType
    counter: number
    ledger: number
    openHolds: number
    delta: number
  }[]
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export interface HealthReport {
  status: 'ok' | 'degraded' | 'down'
  version?: string
  /**
   * True when the API is running in dry run, so nothing leaves the platform.
   *
   * Deliberately a sibling of `checks` rather than one of them: dry run is a
   * setting, not a fault, and it must not colour `status`.
   *
   * Optional because an API that predates the field reports nothing about it.
   * Absence means "not known" and never "sending", so the banner stays silent
   * rather than claiming either way.
   */
  dryRun?: boolean
  /**
   * Whether the model subscription the platform writes with has any allowance
   * left. `spent` stays until something the service writes clears it.
   * `resumeAt` is the time the service said it comes back, and only that, with
   * `resetKnown` true beside it. `nextTryAt` is when a request may next ask the
   * service, and null once the next request may ask straight away.
   *
   * Optional for the same reason `dryRun` is. An API that predates the field
   * says nothing, and the banner then says nothing either. `nextTryAt` is
   * optional inside it because the first build that carried the field sent the
   * probe time in `resumeAt` instead.
   */
  writingAllowance?: {
    state: 'available' | 'spent' | 'unknown'
    resumeAt: string | null
    resetKnown: boolean
    nextTryAt?: string | null
  }
  checks?: Record<string, { ok: boolean; detail?: string } | boolean | string>
}

/** A decline as the prospect page reads it back. */
export interface LastDeclineView {
  reason: string
  note: string | null
  at: IsoDateTime
  /** When the hold lapses or lapsed. Null for a hold that never does. */
  holdUntil: IsoDateTime | null
  liftedAt: IsoDateTime | null
  byName: string | null
}
